import { Howl } from 'howler';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import 'dayjs/locale/id';
import axios from 'axios';
import * as DOMPurify from 'dompurify';
import {
	CURRENCY_SYMBOLS,
	reservedSubdomains,
	NOTIFICATION_TYPES,
	shipmentIcons,
	testerIds,
	supportIds,
	rtsCodes,
	shippingCouriers,
	logisticSupportIds,
	paymentChannels,
	defaultPixelConfig,
	PAYMENT_CHANNELS,
	courierSuccessCodes,
	courierDiscount,
	courierReturnDiscount,
	ticketsType,
	ticketsTopic,
	LOGISTIC_STATUS_HOLD,
	LOGISTIC_STATUS_REJECT,
	LOGISTIC_STATUS_LOST,
	LOGISTIC_STATUS_BROKEN,
	LOGISTIC_STATUS_STUCK,
	LOGISTIC_STATUS_RETURN,
	defaultUom,
} from '@/lib/statics';

import elementOptions from '@/assets/json/funnel-elements.json';


export const getCurrencySymbol = currencyName => CURRENCY_SYMBOLS[currencyName] || currencyName;

export const getMoneyFormat = (currency = 'IDR') => {
	const decimal = currency === 'IDR' ? 0 : 2;
	const decimalSep = currency === 'IDR' ? ',' : '.';
	const thousandSep = currency === 'IDR' ? '.' : ',';
	const format = {
		decimal,
		decimalSep,
		thousandSep,
	};
	return format;
};

export const formatNumber = (number, decimals, decimalSep, thousandsSep) => {
	const validNumber = Number.isNaN(number) ? 0 : number;

	// if decimal is zero we must take it, it means user does not want to show any decimal
	const c = Number.isNaN(decimals) ? 2 : Math.abs(decimals);

	// if no decimal separator is passed we use the dot as default decimal separator
	// (we MUST use a decimal separator)
	const d = decimalSep || '.';

	// if you don't want to use a thousands separator you can pass empty string as thousandsSep value
	const t = (typeof thousandsSep === 'undefined') ? ',' : thousandsSep;

	const sign = (validNumber < 0) ? '-' : '';

	// extracting the absolute value of the integer part of the number and converting to string
	let nX = validNumber;
	const i = parseInt(nX = Math.abs(nX).toFixed(c), 10).toString();

	let j = i.length;
	j = (j > 3) ? j % 3 : 0;

	const x = j ? i.substr(0, j) + t : '';
	const y = i.substr(j).replace(/(\d{3})(?=\d)/g, `$1${t}`);
	const z = c ? d + Math.abs(validNumber - i).toFixed(c).slice(2) : '';

	return sign + x + y + z;
};

export const formatMoney = (number = 0, currency = 'IDR', separateSymbol = false) => {
	const isMinus = number < 0;
	const format = getMoneyFormat(currency);
	const newNumber = isMinus ? number * -1 : number;
	const money = formatNumber(newNumber, format.decimal, format.decimalSep, format.thousandSep);
	const symbol = getCurrencySymbol(currency);
	const separator = separateSymbol ? ' ' : '';
	const formatted = isMinus ? `-${symbol}${separator}${money}` : `${symbol}${separator}${money}`;
	return formatted;
};

// Format Number to display without symbol
export const formatNumberPlain = (number = 0, currency = 'IDR') => {
	const format = getMoneyFormat(currency);
	const money = formatNumber(number, format.decimal, format.decimalSep, format.thousandSep);
	return money;
};

export const formatNumber2 = (num, decpoint, sep) => {
	let numNew = num;
	let decpointNew = decpoint;
	let sepNew = sep;
	// check for missing parameters and use defaults if so
	if (decpointNew === undefined) {
		decpointNew = '.';
	}

	if (sepNew === undefined) {
		sepNew = ',';
	}

	// need a string for operations
	numNew = numNew.toString();

	// separate the whole number and the fraction if possible
	const a = numNew.split(decpointNew);
	let x = a[0]; // decimal
	const y = a[1]; // fraction
	let z = '';

	if (typeof (x) !== 'undefined') {
		// reverse the digits. regexp works from left to right.
		for (let i = x.length - 1; i >= 0; i -= 1) {
			z += x.charAt(i);
		}

		// add separators. but undo the trailing one, if there
		z = z.replace(/(\d{3})/g, `$1${sepNew}`);
		if (z.slice(-sepNew.length) === sepNew) {
			z = z.slice(0, -sepNew.length);
		}

		x = '';
		// reverse again to get back the number
		for (let i = z.length - 1; i >= 0; i -= 1) {
			x += z.charAt(i);
		}

		// add the fraction back in, if it was there
		if (typeof (y) !== 'undefined' && y.length > 0) {
			x += decpointNew + y;
		}
	}

	return x;
};

export const nFormatter = (num, digits, currency) => {
	let si = [];
	if (currency === 'IDR') {
		si = [
			{ value: 1, symbol: '' },
			{ value: 1E3, symbol: 'rb' },
			{ value: 1E6, symbol: 'jt' },
			{ value: 1E9, symbol: 'M' },
			{ value: 1E12, symbol: 'T' },
			{ value: 1E15, symbol: 'P' },
			{ value: 1E18, symbol: 'E' },
		];
	} else {
		si = [
			{ value: 1, symbol: '' },
			{ value: 1E3, symbol: 'k' },
			{ value: 1E6, symbol: 'M' },
			{ value: 1E9, symbol: 'B' },
			{ value: 1E12, symbol: 'T' },
			{ value: 1E15, symbol: 'P' },
			{ value: 1E18, symbol: 'E' },
		];
	}
	const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
	let i;
	for (i = si.length - 1; i > 0; i--) {
		if (num >= si[i].value) {
			break;
		}
	}
	return (num / si[i].value).toFixed(digits).replace(rx, '$1') + si[i].symbol;
};

export const nFormatter2 = (num, digits, currency) => {
	let si = [];
	if (currency === 'IDR') {
		si = [
			{ value: 1, symbol: '' },
			{ value: 1E3, symbol: 'rb' },
			{ value: 1E6, symbol: 'jt' },
			{ value: 1E9, symbol: 'M' },
			{ value: 1E12, symbol: 'T' },
			{ value: 1E15, symbol: 'P' },
			{ value: 1E18, symbol: 'E' },
		];
	} else {
		si = [
			{ value: 1, symbol: '' },
			{ value: 1E3, symbol: 'k' },
			{ value: 1E6, symbol: 'M' },
			{ value: 1E9, symbol: 'B' },
			{ value: 1E12, symbol: 'T' },
			{ value: 1E15, symbol: 'P' },
			{ value: 1E18, symbol: 'E' },
		];
	}
	const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
	let i;
	for (i = si.length - 1; i > 0; i--) {
		if (num >= si[i].value) {
			break;
		}
	}
	const theValue = (num / si[i].value).toFixed(digits).replace(rx, '$1');
	const formatted = {
		value: theValue,
		symbol: si[i].symbol,
	};
	return formatted;
};

export const formatMoneyShort = (number, currency = 'IDR', digits = 2) => {
	const symbol = getCurrencySymbol(currency);
	const money = nFormatter(number, digits, currency);
	const formatted = `${symbol}${money}`;
	return formatted;
};

export const duplicateVar = value => (value ? JSON.parse(JSON.stringify(value)) : '');

/**
 * Slugify
 * @see https://gist.github.com/mathewbyrne/1280286
*/
export const slugify = text => text.toString().toLowerCase()
	.replace(/\s+/g, '-') // Replace spaces with -
	// eslint-disable-next-line
	.replace(/[^\w\-]+/g, '') // Remove all non-word chars
	// eslint-disable-next-line
	.replace(/\-\-+/g, '-') // Replace multiple - with single -
	.replace(/^-+/, ''); // Trim - from start of text
	// .replace(/-+$/, '');

/**
 * Build Query String
 * @param {Object} params
 * @see https://stackoverflow.com/a/34209399/5627904
*/
export const buildQuery = (params) => {
	const esc = encodeURIComponent;
	const query = Object.keys(params)
		.map(k => `${esc(k)}=${esc(params[k])}`)
		.join('&');
	return query;
};

/**
 * Detect Mobile Browser
 * @see https://stackoverflow.com/a/11381730/5627904
 */
export const isMobile = () => {
	let check = false;
	// eslint-disable-next-line
	(function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; }(navigator.userAgent || navigator.vendor || window.opera));
	return check;
};

/**
 * Detect Mobile and Tab Browser
 * @see https://stackoverflow.com/a/11381730/5627904
 */
export const isMobileOrTablet = () => {
	let check = false;
	// eslint-disable-next-line
	(function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
	return check;
};

/**
* Get YouTube ID from various YouTube URL
* @author takien
* @see http://takien.com
*/
export const getYoutubeId = (url) => {
	let ID = '';
	const theUrl = url.replace(/(>|<)/gi, '').split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
	if (theUrl[2] !== undefined) {
		// eslint-disable-next-line
		ID = theUrl[2].split(/[^0-9a-z_\-]/i);
		ID = ID[0];
	} else {
		ID = theUrl;
	}
	return ID;
};

// YouTube Parser
export const youtubeParser = (url) => {
	const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
	const match = url.match(regExp);
	return (match && match[7].length === 11) ? match[7] : false;
};

// GET YOUTUBE THUMBNAIL FROM YOUTUBE URL
export const getYoutubeThumb = (url, size) => {
	if (url === null || url === '') {
		return '';
	}
	size = (size === null) ? 'big' : size;
	const results = url.match('[\\?&]v=([^&#]*)');
	const video = (results === null) ? url : results[1];

	if (size === 'small') {
		return `https://img.youtube.com/vi/${video}/2.jpg`;
	}
	return `https://img.youtube.com/vi/${video}/0.jpg`;
};

/**
 * Is Valid URL
 * @see https://www.w3resource.com/javascript-exercises/javascript-regexp-exercise-9.php
 */
export const isValidURLStrict = (str) => {
	const pattern = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
	return pattern.test(str);
};

/**
 * Is Valid URL
 * @param {string} str URL
 * @see https://stackoverflow.com/a/55952338/5627904
 */
export const isValidURL = (str) => {
	const pattern = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
	// const pattern = new RegExp('^((https?:)?\\/\\/)?' // protocol
	// // + '(?:\\S+(?::\\S*)?@)?' // authentication
	// + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
	// + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
	// + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
	// + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
	// + '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locater
	return str && str !== '' ? pattern.test(str) : false;
};

/**
 * Is Valid Youtube URL
 * @param {string} str URL
 * @see https://stackoverflow.com/a/28735961
 */
export const isValidYoutubeUrl = (url) => {
	const p = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
	const matches = p.test(url);
	if (matches) {
		return true;
	}
	return false;
};

/**
 * Is Number
 * @description Check if string is contain number only
 */
export const isNumber = (str) => {
	const reg = new RegExp(/^\d+$/);
	const isNumber = reg.test(str);
	return isNumber;
};

/**
 * Delay
 */
export const delay = (function startDelay() {
	let timer = 0;
	const call = function (callback, ms) {
		clearTimeout(timer);
		timer = setTimeout(callback, ms);
	};
	return call;
}());

/**
 * Email Validation
 */
export const isValidEmail = (email) => {
	const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(email);
};

export const isValidSubdomain = (name) => {
	if (name === '') {
		return false;
	}

	const index = reservedSubdomains.indexOf(name);

	if (index !== -1) {
		return false;
	}

	const patterns = [
		'db[0-9]+',
		'dc[0-9]+',
		'dev[0-9]+',
		'dns[0-9]+',
		'ftp[0-9]+',
		'host[0-9]+',
		'm[0-9]+',
		'mail[0-9]+',
		'mx[0-9]+',
		'ns[0-9]+',
		'server-[0-9]+',
		'server[0-9]+',
		'smtp[0-9]+',
		'static[0-9]+',
		'test[0-9]+',
		'v[0-9]+',
		'vpn[0-9]+',
		'web[0-9]+',
		'ww[a-z0-9-]+',
	];

	const patternsRegex = patterns.map(pattern => new RegExp(pattern));

	for (let i = 0; i < patternsRegex.length; ++i) {
		const regex = patternsRegex[i];

		if (regex.test(name)) {
			return false;
		}
	}

	return true;
};

export const triggerEvent = (el, type) => {
	if ('createEvent' in document) {
		// modern browsers, IE9+
		const e = document.createEvent('HTMLEvents');
		e.initEvent(type, false, true);
		el.dispatchEvent(e);
	} else {
		// IE 8
		const e = document.createEventObject();
		e.eventType = type;
		el.fireEvent(`on${e.eventType}`, e);
	}
};

/**
 * Insert Text at Textarea Cursor
 *
 * @param {HTMLTextAreaElement} myField
 * @param {String} myValue
 *
 * @see https://stackoverflow.com/a/47850725/5627904
 */
export const insertAtCursor = (field, myValue) => {
	// IE support
	if (document.selection) {
		field.focus();
		const sel = document.selection.createRange();
		sel.text = myValue;
	} else if (window.navigator.userAgent.indexOf('Edge') > -1) { // Microsoft Edge
		const startPos = field.selectionStart;
		const endPos = field.selectionEnd;

		field.value = field.value.substring(0, startPos) + myValue + field.value.substring(endPos, field.value.length);

		const pos = startPos + myValue.length;
		field.focus();
		field.setSelectionRange(pos, pos);
	} else if (field.selectionStart || field.selectionStart === 0) { // MOZILLA and others
		const startPos = field.selectionStart;
		const endPos = field.selectionEnd;
		field.value = field.value.substring(0, startPos) + myValue + field.value.substring(endPos, field.value.length);
	} else {
		field.value += myValue;
	}
	triggerEvent(field, 'input');
};

export const formatMonth = (month, lang = 'ID') => {
	const months = {
		ID: ['', 'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'],
		US: ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
	};
	return months[lang][month];
};

export const getAxiosErrorMessage = e => (e.response ? e.response.data.message : '');
export const getAxiosErrorValidation = e => (e.response ? e.response.data : '');

export const getCheckoutUrl = (username, productSlug) => {
	const isProd = process.env.NODE_ENV === 'production';
	const protocol = isProd ? 'https' : 'http';
	const domain = process.env.VUE_APP_PUBLIC_DOMAIN || 'ostock.id';
	const domainFull = isProd ? `${protocol}://${username}.${domain}` : `${protocol}://${domain}/published/${username}`;
	const checkoutUrl = productSlug ? `${domainFull}/${productSlug}` : domainFull;
	return checkoutUrl;
};

// Create Combinations
export const createCombinations = (data) => {
	const result = data.reduce((a, b) => a.reduce((r, v) => r.concat(b.map(w => [].concat(v, w))), []));
	// const result = data.reduce(
	// 	(a, b) => a.reduce(
	// 		(r, v) => r.concat(b.map(w => [].concat(v, w))),
	// 		[],
	// 	),
	// );
	return result;
};

export const generateCombinations = (variations) => {
	const attributesOptions = variations.attributes.map(attribute => attribute.values);
	const hasAttributes = attributesOptions.length > 0;
	const combinations = hasAttributes ? createCombinations(attributesOptions) : [];
	return combinations;
};

// Parse Number
export const parseNumber = (str, defaultValue = 0) => {
	if (typeof str === 'number') {
		return str;
	}
	let parsed = str.replace(/[^0-9]/g, '').trim();
	const parsedLength = parsed.length;
	if (parsedLength === 0) {
		parsed = defaultValue;
	} else if (parsedLength > 1 && parsed.charAt(0) === '0') {
		parsed = parsed.substring(1, parsedLength);
	}
	parsed = parseInt(parsed);
	return parsed;
};

/**
 * Convert Image URL into Base64 Data
 *
 * @param url Image URL
 * @param callback Retrieve Base64 as Param
 *
 * @see https://stackoverflow.com/a/50155915/5627904 StackOverflow
 */
export const imageURLtoBase64 = (url, callback) => {
	const img = new Image();
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	// const imageUrl = `https://cors-anywhere.herokuapp.com/${url}`;

	// const x = new XMLHttpRequest();
	// console.log(`https://cors-anywhere.herokuapp.com/${url}`);
	// x.open('GET', `https://cors-anywhere.herokuapp.com/${url}`);
	// // I put "XMLHttpRequest" here, but you can use anything you want.
	// x.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
	// x.onload = () => {
	// 	console.log(x.responseText);
	// };
	// x.send();

	const retryMax = 4;
	let retryCount = 0;

	img.crossOrigin = 'Anonymous';
	// img.setAttribute('crossOrigin', 'Anonymous');

	img.onload = () => {
		canvas.width = img.width;
		canvas.height = img.height;
		ctx.drawImage(img, 0, 0);
		const dataURL = canvas.toDataURL('image/png');
		const image = {
			data: dataURL,
			width: img.width,
			height: img.height,
		};
		callback(image);
	};

	// On Download Error
	img.onerror = () => {
		setTimeout(() => {
			retryCount++;
			if (retryCount <= retryMax) {
				img.src = url;
			}
		}, 1000);
	};

	img.src = url;
};

// Image Elem to Base64
export const imageElemtoBase64 = (img) => {
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	const width = img.naturalWidth;
	const height = img.naturalHeight;
	canvas.width = width;
	canvas.height = height;
	ctx.drawImage(img, 0, 0);
	const dataURL = canvas.toDataURL('image/png');
	const image = {
		data: dataURL,
		width,
		height,
	};
	return image;
};

export const isHighResolutionScreen = () => window.devicePixelRatio > 1;

/**
 * Make PDF with html2canvas and jsPDF
 *
 * @param doc Document
 * @param id Element ID
 * @param h2c html2canvas Library
 * @param jsPDF jsPDF Library
 *
 * @see https://stackoverflow.com/a/42299908/5627904
 */
export const makePDF = (doc, id, h2c, jsPDF, name, callback) => {
	const isRetina = isHighResolutionScreen();
	const baseWidth = 960;
	const baseHeight = 1280;

	const configWidth = isRetina ? baseWidth * 2 : baseWidth;
	const configHeight = isRetina ? baseHeight * 2 : baseHeight;
	// console.log(`Pixel Ratio: ${window.devicePixelRatio}`, `Width: ${configWidth}`, `Height: ${configHeight}`);

	const config = {
		page: {
			width: configWidth,
			height: configHeight,
		},
	};

	const quotes = doc.getElementById(id);

	const callbackCanvas = (canvas) => {
		//! MAKE YOUR PDF
		// eslint-disable-next-line
		const pdf = new jsPDF('p', 'pt');

		const pageWidth = config.page.width;
		const pageHeight = config.page.height;

		for (let i = 0; i <= quotes.clientHeight / pageHeight; i++) {
			//! This is all just html2canvas stuff
			const srcImg = canvas;
			const sX = 0;
			const sY = pageHeight * i; // start 980 pixels down for every new page
			const sWidth = pageWidth;
			const sHeight = pageHeight;
			const dX = 0;
			const dY = 0;
			const dWidth = pageWidth;
			const dHeight = pageHeight;

			const onePageCanvas = doc.createElement('canvas');
			onePageCanvas.setAttribute('width', sWidth);
			onePageCanvas.setAttribute('height', sHeight);
			onePageCanvas.style.width = `${baseWidth}px`;
			onePageCanvas.style.height = `${baseHeight}px`;
			const ctx = onePageCanvas.getContext('2d');
			if (isRetina) {
				ctx.scale(0.5, 0.5);
			}
			// ctx.webkitImageSmoothingEnabled = false;
			// ctx.mozImageSmoothingEnabled = false;
			// ctx.imageSmoothingEnabled = false;
			// details on this usage of this function:
			// https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Using_images#Slicing
			ctx.drawImage(srcImg, sX, sY, sWidth, sHeight, dX, dY, dWidth, dHeight);

			// document.body.appendChild(canvas);
			const canvasDataURL = onePageCanvas.toDataURL('image/png', 1.0);

			const width = onePageCanvas.width;
			const height = onePageCanvas.clientHeight;

			//! If we're on anything other than the first page,
			// add another page
			if (i > 0) {
				pdf.addPage();
				// pdf.addPage(612, 791); // 8.5" x 11" in pts (in*72)
				// pdf.addPage(595.44, 841.68); // A4 8.27" x 11.69" in pts (in*72)
			}
			//! now we declare that we're working on that page
			pdf.setPage(i + 1);
			//! now we add content to that page!
			// pdf.addImage(canvasDataURL, 'PNG', 20, 10, (width * 0.62), (height * 0.62));
			pdf.addImage(canvasDataURL, 'PNG', 0, 0, (width * 0.62), (height * 0.62));
		}
		//! after the for loop is finished running, we save the pdf.
		const filename = `${name}.pdf`;
		pdf.save(filename);

		if (callback) {
			callback(filename);
		}
	};

	const errorCallbackCanvas = (e) => {
		// eslint-disable-next-line
		console.log(e);
	};

	const h2cConfig = {
		scrollX: 0,
		scrollY: 0,
		width: configWidth,
		height: configHeight,
	};

	h2c(quotes, h2cConfig).then(callbackCanvas).catch(errorCallbackCanvas);
};

/**
 * Download File URL
 */
export const downloadFile = (url, newTab = false) => {
	const a = document.createElement('A');
	if (newTab) {
		a.target = '_blank';
	}
	a.href = url;
	a.download = url.substr(url.lastIndexOf('/') + 1);
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
};

// nl2br
export const nl2br = (str) => {
	const breakTag = '<br>';
	return (`${str}`).replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, `$1${breakTag}$2`);
};

// Get Date Range
export const getDateRange = (startOffset = -30, endOffset = 0) => {
	const today = new Date();
	// const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 1);
	// const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
	// const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
	// const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + startOffset);
	const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + endOffset);

	const filterDate = {
		start: startDate,
		end: endDate,
	};

	return filterDate;
};

// Generate Date Range
// From: https://stackoverflow.com/a/64592438/5627904
// const dates = generateDateRange('2020-09-27', '2020-10-28');
export const generateDateRange = (startDate, endDate, steps = 1) => {
	const dateArray = [];
	const currentDate = new Date(startDate);

	while (currentDate <= new Date(endDate)) {
		dateArray.push(new Date(currentDate));
		// Use UTC date to prevent problems with time zones and DST
		currentDate.setUTCDate(currentDate.getUTCDate() + steps);
	}

	return dateArray;
};

/**
 * Smooth Scroll To Element
 *
 * @param {string} id Element ID
 * @param {number} [offset=0] Offset Top
 */
export const scrollTo = (id, offset = 0) => {
	const el = document.getElementById(id);
	if (el) {
		const pos = el.style.position;
		const top = el.style.top;
		el.style.position = 'relative';
		el.style.top = `${offset}px`;
		el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		el.style.top = top;
		el.style.position = pos;
	}
};

export const isVariableChanged = (obj1, obj2) => {
	const string1 = JSON.stringify(obj1);
	const string2 = JSON.stringify(obj2);
	return string1 !== string2;
};

/**
 *
 * @param {string} str String to be splitted
 * @param {number} l Split length
 *
 * @returns {string[]} Splitted strings
 */
export const textSplitter = (str, l) => {
	const strs = [];
	let theString = str;
	while (theString.length > l) {
		const newlinePosition = theString.substring(0, l).lastIndexOf('\n');
		const isGotNewline = newlinePosition !== -1;
		// If there is a newline within the text range, then get the newline position
		let pos = isGotNewline ? newlinePosition : theString.substring(0, l).lastIndexOf(' ');
		pos = pos <= 0 ? l : pos;
		strs.push(theString.substring(0, pos));
		if (isGotNewline) {
			strs.push('');
		}
		let i = isGotNewline ? theString.indexOf('\n', pos) + 1 : theString.indexOf(' ', pos) + 1;
		if (i < pos || i > pos + l) i = pos;
		theString = theString.substring(i);
	}
	strs.push(theString);
	return strs;
};

/**
 * Check if variable is a Date
 * @param {Date} d Date Variable
 * @returns {boolean} Is valid date
 */
// export const isValidDate = d => d instanceof Date && !Number.isNaN(d);
export const isValidDate = d => new Date(d).toString() !== 'Invalid Date';

/**
 * Get Image Base 64 data from URL
 * @param {string} url Image URL
 */
export const getImageData = (url) => {
	const promise = new Promise((resolve) => {
		const callback = (image) => {
			resolve(image);
		};
		imageURLtoBase64(url, callback);
	});
	return promise;
};

export const isChrome = () => navigator.userAgent.toLowerCase().indexOf('chrome') > -1;
export const isFirefox = () => navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
export const isWindows = () => navigator.platform.indexOf('Win') > -1;
export const isMac = () => navigator.platform.indexOf('Mac') > -1;
export const isLinux = () => navigator.platform.indexOf('Linux') > -1;

// Convert String to Title Case
export const toTitleCase = (str) => {
	const string = (str === null || str === '') ? '-' : str;
	return string.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

/**
 * Group Array By Name
 *
 * @param {Object[]} arr Array of Objects
 * @param {string} property Object Property Key
 */
export const groupBy = (arr, property) => {
	const reducer = (memo, x) => {
		const theMemo = memo;
		if (!theMemo[x[property]]) { theMemo[x[property]] = []; }
		theMemo[x[property]].push(x);
		return theMemo;
	};
	return arr.reduce(reducer, {});
};

// Group Orders by Courier
export const groupOrdersByCourier = (arr) => {
	const reducer = (memo, x) => {
		const theMemo = memo;
		const courier = x?.shipping?.courier ?? 'other';
		if (!theMemo[courier]) { theMemo[courier] = []; }
		theMemo[courier].push(x);
		return theMemo;
	};
	return arr.reduce(reducer, {});
};

// Get Wholesale Price
export const getWholesalePrice = (wholesales, quantity, normalPrice = 0) => {
	let priceValue = normalPrice;
	if (wholesales) {
		for (let index = 0; index < wholesales.length; index++) {
			const wholesale = wholesales[index];
			if (wholesale.min !== '' && wholesale.price !== '' && quantity >= wholesale.min) {
				priceValue = wholesale.price;
			}
		}
	}
	return priceValue;
};

/**
 * Determine if an element is in the viewport
 * (c) 2017 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node}		elem The element
 * @return {Boolean}	Returns true if element is in the viewport
 * @see https://gomakethings.com/how-to-test-if-an-element-is-in-the-viewport-with-vanilla-javascript/
 */
export const isInViewport = (elem) => {
	const distance = elem.getBoundingClientRect();
	return (
		distance.top >= 0
		&& distance.left >= 0
		&& distance.bottom <= (window.innerHeight || document.documentElement.clientHeight)
		&& distance.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
};

/**
 * Get Add On Price
 * @param {Object[]} pricing Add On Pricings
 */
export const getAddOnPrice = (pricing) => {
	const firstPricing = pricing[0];
	const price = firstPricing ? firstPricing.price : 0;
	const priceFormatted = formatMoney(price);
	const prefix = pricing.length > 1 ? 'From ' : '';
	const priceLabel = price > 0 ? `${prefix}${priceFormatted}` : 'Free';
	return priceLabel;
};

// Format Date
export const formatDate = (moment, dateString, format = 'DD-MM-YYYY', timezone = 'Asia/Jakarta') => {
	let result = dateString;
	if (result === '-') {
		result = '-';
		return result;
	}
	const date = moment.utc(dateString);
	const formattedDate = timezone ? date.tz(timezone).format(format) : date.local().format(format);
	result = dateString && dateString !== '' ? formattedDate : '';

	return result;
};

// min date
export const dateToday = () => {
	const today = new Date();
	let day = today.getDate();
	day = day.toString();
	// January is 0!
	let month = today.getMonth() + 1;
	month = month.toString();
	const mm = month.length >= 2 ? month : `0${month}`;
	const dd = day.length >= 2 ? day : `0${day}`;
	const yyyy = today.getFullYear();
	const date = `${yyyy}-${mm}-${dd}`;
	return date;
};

/**
 * Get Product's Total Stock
 * @param {Object} product Product
 */
export const getProductStock = (product) => {
	const isSimple = product.type === 'simple';
	let stock = isSimple && product.stock ? product.stock.quantity : 0;
	if (!isSimple) {
		const reducer = (stockTotal, variationPrice) => {
			let total = stockTotal;
			const stockQty = variationPrice.stock ? variationPrice.stock.quantity : 0;
			total += stockQty;
			return total;
		};
		stock = product.variations.prices.reduce(reducer, 0);
	}
	return stock;
};

// Copy to Clipboard
export const copyToClipboard = (str) => {
	const el = document.createElement('textarea');	// Create a <textarea> element
	el.value = str;									// Set its value to the string that you want copied
	el.setAttribute('readonly', '');				// Make it readonly to be tamper-proof
	el.style.position = 'absolute';
	el.style.left = '-9999px';						// Move outside the screen to make it invisible
	document.body.appendChild(el);					// Append the <textarea> element to the HTML document
	const selected = document.getSelection().rangeCount > 0		// Check if there is any content selected previously
		? document.getSelection().getRangeAt(0)	// Store selection if found
		: false;								// Mark as false to know no selection existed before
	el.select();									// Select the <textarea> content
	document.execCommand('copy');					// Copy - only works as a result of a user action (e.g. click events)
	document.body.removeChild(el);					// Remove the <textarea> element
	if (selected) {									// If a selection existed before copying
		document.getSelection().removeAllRanges();	// Unselect everything on the HTML document
		document.getSelection().addRange(selected);	// Restore the original selection
	}
};

// Play Sound
export const playSound = (filename) => {
	// const audio = new Audio(filename);
	const audio = new Howl({
		src: [filename],
	});
	audio.play();
};

export const getSerializedSVG = (id) => {
	let serializedSVG = false;
	const element = document.getElementById(id);
	if (element) {
		const serializer = new XMLSerializer();
		serializedSVG = serializer.serializeToString(element);
	}
	return serializedSVG;
};

/**
 * Generate Base64 from SVG Element
 *
 * @see https://stackoverflow.com/a/28450879/5627904
 */
export const generateBase64FromSVG = (id) => {
	let base64 = '';
	const serializedSVG = getSerializedSVG(id);
	if (serializedSVG) {
		const encodedData = window.btoa(serializedSVG);
		base64 = `data:image/svg+xml;base64,${encodedData}`;
	}
	return base64;
};

/**
 * Get Embed Code
 * @param {Object} args Arguments
 * @param {string} args.username Username
 * @param {string} args.productSlug Product Slug
 * @param {string} args.formId Form ID
 * @param {string} args.formElementId Form Element ID
 * @param {string} args.redirectDomain Redirect Domain
 * @param {string} version Code Version
 * @return {string} Code
 */
export const getEmbedCode = (args, version = '5.6.6') => {
	const {
		username, productSlug, formId, formElementId, redirectDomain, configs,
	} = args;
	const code = `<!-- Ostock Embed Code - v${version} -->
<div class="ooef">
	<form class="orderonline-embed-form" data-username="${username}" data-product-slug="${productSlug}" id="${formElementId}">
		<div class="ooef-loader"><style>.ooef-loader{visibility: hidden;opacity: 0;position: absolute;left: 0;right: 0;top: 0;bottom: 0;display: flex;justify-content: center;align-items: center;flex-direction: column;animation: ooLoadingIn 10s ease;-webkit-animation: ooLoadingIn 10s ease;animation-fill-mode: forwards;overflow: hidden}@keyframes ooLoadingIn{0%{visibility: hidden;opacity: 0}20%{visibility: visible;opacity: 0}100%{visibility: visible;opacity: 1}}@-webkit-keyframes ooLoadingIn{0%{visibility: hidden;opacity: 0}20%{visibility: visible;opacity: 0}100%{visibility: visible;opacity: 1}}.ooef-loader>div,.ooef-loader>div:after{border-radius: 50%;width: 2.5rem;height: 2.5rem}.ooef-loader>div{font-size: 10px;position: relative;text-indent: -9999em;border: .25rem solid #f5f5f5;border-left: .25rem solid #55c4cf;-webkit-transform: translateZ(0);-ms-transform: translateZ(0);transform: translateZ(0);-webkit-animation: ooLoading 1.1s infinite linear;animation: ooLoading 1.1s infinite linear}.ooef-loader.error>div{border-left: .25rem solid #ff4500;animation-duration: 5s}@-webkit-keyframes ooLoading{0%{-webkit-transform: rotate(0);transform: rotate(0)}100%{-webkit-transform: rotate(360deg);transform: rotate(360deg)}}@keyframes ooLoading{0%{-webkit-transform: rotate(0);transform: rotate(0)}100%{-webkit-transform: rotate(360deg);transform: rotate(360deg)}}</style><div aria-live="polite" role="status"><div>Loading...</div></div></div>
	</form>
</div>
<script>
	if (typeof ooLogError != 'function') {
		var ooLogError = function (error) {
			var req = new XMLHttpRequest();
			var payload = JSON.stringify({ url: document.location.href, line: error.line, stack: error.stack });
			var params = 'message=' + encodeURIComponent(error.name) + '&payload=' + encodeURIComponent(payload) + '&type=embed&level=error';
			req.open('POST', 'https://api.ostock.id/log', true);
			req.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
			req.send(params);
		};
	}
	try {
		if (typeof ooEmbedScript != 'function') {
			var ooEmbedScript = function () {
				!function(w, d, e, v, id, t, s) {
				if (d.getElementById(id)) return;
				t = d.createElement(e);t.async = !0;t.src = v;t.id = id;
				s = d.getElementsByTagName(e)[0];
				s.parentNode.insertBefore(t, s);
				}(window, document, 'script', 'https://cdn.orderonline.id/js/embed-slim.min.js?v=${version}', 'oo-embed-js');
			};
		}
		if (typeof orderOnlineInit != 'function') {
			var orderOnlineInit = function (w, n) {
				if (w.ooe) return;
				n = w.ooe = function() { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) };
				if (!w._ooe) w._ooe = n;
				n.push = n;n.loaded = !0;n.version = '${version}';n.queue = [];
			};
		}
		orderOnlineInit(window);
		ooe('setup', 'redirect', '${redirectDomain}');
		ooe('init', '${username}', '${productSlug}', ${formId}, '${formElementId}', ${JSON.stringify(configs)});
		if (!window.jQuery) {
			!function(w, d, e, v, id, t, s) {
			if (d.getElementById(id)) return;
			t = d.createElement(e);t.async = !0;t.src = v;t.id = id;
			t.addEventListener('load', ooEmbedScript);
			s = d.getElementsByTagName(e)[0];
			s.parentNode.insertBefore(t, s);
			}(window, document, 'script', 'https://cdn.orderonline.id/js/vendor/jquery.min.js', 'oo-embed-jquery');
		} else {
			ooEmbedScript();
		}
	} catch (e) {
		ooLogError(e);
		throw e;
	}
</script>
<!-- End Ostock Embed Code -->`;
	return code;
};

/**
 * Get Simple Embed Code
 * @param {Object} args Arguments
 * @param {string} args.username Username
 * @param {string} args.productSlug Product Slug
 * @param {string} args.formId Form ID
 * @param {string} args.formElementId Form Element ID
 * @param {string} args.redirectDomain Redirect Domain
 */
export const getSimpleEmbedCode = (args) => {
	const {
		username, productSlug, formId, formElementId, redirectDomain,
	} = args;
	const code = `<!-- Ostock Embed Code - v1.0 -->
<form class="orderonline-embed-form" data-username="${username}" data-product-slug="${productSlug}" id="${formElementId}"></form>
<script>
	!function(w, d, e, v, id, n, t, s) {
	if (w.ooe) return;
	n = w.ooe = function() { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) };
	if (!w._ooe) w._ooe = n;
	n.push = n;n.loaded = !0;n.version = '1.0';n.queue = [];
	if (d.getElementById(id)) return;
	t = d.createElement(e);t.async = !0;t.src = v;t.id = id;
	s = d.getElementsByTagName(e)[0];
	s.parentNode.insertBefore(t, s);
	}(window, document, 'script', 'https://cdn.orderonline.id/js/embed.bundle.min.js', 'oo-embed-js');
	ooe('setup', 'redirect', '${redirectDomain}');
	ooe('init', '${username}', '${productSlug}', ${formId}, '${formElementId}');
</script>
<!-- End Ostock Embed Code -->`;
	return code;
};

// Get Logistic Notif Message
export const getLogisticNotifMessage = (data) => {
	const status = data.status;
	const statusLabel = toTitleCase(status);
	const receiptNumber = data.receipt_number;

	let message = `No. Resi: ${receiptNumber}, Status: ${statusLabel}.`;

	if (status === LOGISTIC_STATUS_HOLD) {
		message = `No. Resi: ${receiptNumber} mengalami masalah pengiriman! Klik disini untuk menangani sekarang!`;
	} else if (status === LOGISTIC_STATUS_REJECT) {
		message = `No. Resi: ${receiptNumber} ditolak oleh penerima! Klik disini untuk menangani sekarang!`;
	} else if (status === LOGISTIC_STATUS_LOST) {
		message = `No. Resi: ${receiptNumber} hilang dalam proses pengiriman! Klik disini untuk menangani sekarang!`;
	} else if (status === LOGISTIC_STATUS_BROKEN) {
		message = `No. Resi: ${receiptNumber} rusak dalam proses pengiriman! Klik disini untuk menangani sekarang!`;
	} else if (status === LOGISTIC_STATUS_STUCK) {
		message = `No. Resi: ${receiptNumber} tidak bergerak! Klik disini untuk menangani sekarang!`;
	} else if (status === LOGISTIC_STATUS_RETURN) {
		message = `No. Resi: ${receiptNumber} dikembalikan, pastikan barang sampai ke alamat Anda!`;
	}

	return message;
};

// Get Notif Message
export const getNotifMessage = (notif, textOnly = false) => {
	const data = notif.data;
	const customerName = data && data.customer_name ? data.customer_name : 'Seseorang';
	const count = data && data.reminder_count ? data.reminder_count : 0;
	let message = '';

	let productName = data && data.product_name ? data.product_name : '';

	switch (notif.type) {
	case NOTIFICATION_TYPES.ORDER_NEW:
		productName = data
			&& data.type === 'storefront'
			&& data.product_name === '' ? 'beberapa produk' : data.product_name;
		message = `${data.name} memesan ${productName}`;
		break;

	case NOTIFICATION_TYPES.PAYMENT_NEW:
		if (data.type === 'storefront') {
			message = `${data.name} telah melakukan pembayaran`;
			if (data.order_id) {
				message += ` untuk Order: <b>#${data.order_id}</b>`;
			}
		} else {
			message = `${data.name} telah melakukan pembayaran untuk produk ${data.product_name}`;
		}
		break;

	case NOTIFICATION_TYPES.ORDER_PROCESSED:
		message = `Pesanan ${data.product_name} dari ${data.name} telah dibayar`;
		break;

	case NOTIFICATION_TYPES.FB_TOKEN_EXPIRED:
		message = 'Access token anda telah kedaluwarsa, silahkan integrasikan ulang akun Facebook anda';
		break;

	case NOTIFICATION_TYPES.ORDER_LIMIT:
		message = 'Jumlah order anda telah mencapai limit';
		break;

	case NOTIFICATION_TYPES.PAYMENT_CONFIRMATION:
		message = `${customerName} telah melakukan konfirmasi pembayaran sejumlah <b>${formatMoney(data.transfer_amount)}</b>`;
		break;

	case NOTIFICATION_TYPES.CONTACT_REMINDER:
		message = `Anda memiliki ${count} pengingat`;
		break;

	case NOTIFICATION_TYPES.BILLING_NEW:
		message = `Anda memiliki tagihan baru "${data.description}" sebesar ${formatMoney(data.value)}`;
		break;

	case NOTIFICATION_TYPES.WALLET_HISTORY_NEW:
		message = `Anda memiliki transaksi baru "${data.description}" sebesar ${formatMoney(data.value)} (${data.mutation})`;
		break;

	case NOTIFICATION_TYPES.PRODUCT_SUSPENDED:
		message = `Produk "${data.product_name}" telah ditangguhkan.`;
		break;

	case NOTIFICATION_TYPES.PRODUCT_REQUESTED:
		message = `Produk "${data.product_name}" perlu ditinjau.`;
		break;

	case NOTIFICATION_TYPES.PRODUCT_PRICING:
		message = `Produk Baru "${data.product_name}" perlu ditinjau.`;
		break;

	case NOTIFICATION_TYPES.PRODUCT_UPDATED:
		message = `Produk "${data.product_name}" perlu ditinjau.`;
		break;

	case NOTIFICATION_TYPES.PRODUCT_IMPORTED:
		message = `Supplier "${data.store_name}" mengimport ${data.product_count} Produk Baru`;
		break;

	case NOTIFICATION_TYPES.PRODUCT_INVITATION:
		message = `Anda diundang untuk menjual produk "${data.product_name}"`;
		break;

	case NOTIFICATION_TYPES.PRODUCT_STOCK:
		message = `Produk "${data.product_name}" diprediksi akan habis dalam ${data.remaining_days ? data.remaining_days : ''} hari.`;
		break;

	case NOTIFICATION_TYPES.EMPTY:
		message = 'Anda tidak memiliki notifikasi baru';
		break;

	case NOTIFICATION_TYPES.WHATSAPP_FAILED:
		message = `Pengiriman pesan ${data.wa_number ? (`ke no ${data.wa_number}`) : ''} gagal.`;
		break;

	case NOTIFICATION_TYPES.NEW_DROPSHIPPER:
		message = `${data.name} baru saja mendaftar sebagai Seller`;
		break;

	case NOTIFICATION_TYPES.NEW_SUPPLIER:
		message = `${data.name} baru saja mendaftar sebagai Supplier`;
		break;

	default:
		break;
	}

	if (notif.type === NOTIFICATION_TYPES.WALLET_HISTORY_NEW) {
		let description = data.description && data.description !== ''
			? data.description : '';
		if (description === '' && data.type === 'shipping_cashback') {
			const dateInfo = dayjs(notif.created_at)
				.subtract(1, 'month')
				.locale('id')
				.format('MMMM YYYY');
			description = `Cashback dari biaya pengiriman bulan ${dateInfo}`;
		}
		message = `Anda memiliki transaksi baru "${description}" sebesar ${formatMoney(data.value)} (${data.mutation})`;
	} else if (notif.type === NOTIFICATION_TYPES.WALLET_HISTORY_UPDATE) {
		message = data.description;
		if (data.status) {
			message += ` is ${data.status}`;
		}
	} else if (notif.type === NOTIFICATION_TYPES.WITHDRAWAL_NEW) {
		message = data.description;
		if (data.status) {
			message += ` is ${data.status}`;
		}
	} else if (notif.type === NOTIFICATION_TYPES.BILLING_PAYMENT) {
		const amount = formatMoney(data.value);
		message = `${data.description}. Amount: ${amount}`;
	} else if (notif.type === NOTIFICATION_TYPES.NEW_LOGISTIC) {
		const orderId = data.order_id || '-';
		const status = toTitleCase(data.status);
		message = `Order ID: #${orderId}, No. Resi: ${data.receipt_number}, Status: ${status}`;
	} else if (notif.type === NOTIFICATION_TYPES.LOGISTIC_UPDATE) {
		const description = data.description || '-';
		const status = toTitleCase(data.status);
		message = `No. Resi: ${data.receipt_number}, Status: ${status}, ${description}`;
	} else if (notif.type === NOTIFICATION_TYPES.LOGISTIC) {
		const description = data.description ? data.description : '';
		// const status = toTitleCase(data.status);
		const isSolved = data.is_solved;
		const logisticMessage = getLogisticNotifMessage(data);
		if (textOnly) {
			if (isSolved) {
				message = `No. Resi: ${data.receipt_number}, Solved.`;
			} else {
				message = logisticMessage;
			}
		} else if (!textOnly) {
			if (isSolved) {
				message = /* html */ `
					<div>No. Resi: ${data.receipt_number}, <b>Solved</b></div>
				`;
			} else {
				message = /* html */ `
					<div>${logisticMessage}</div>
				`;
				if (description && description !== '') {
					message = /* html */ `
						${message}
						<div class="well my-2 bg-white text-info">${description}</div>
					`;
				}
			}
		}
	}

	return message;
};

// Get Notif Link
export const getNotifLink = (notif) => {
	let link = process.env.VUE_APP_URL || 'https://ostock.id';
	if (notif.type === NOTIFICATION_TYPES.ORDER_NEW) {
		const orderId = notif.data.submission_id.$oid;
		link += `/orders/${orderId}`;
	} else if (notif.type === NOTIFICATION_TYPES.BILLING_NEW) {
		link += '/billings';
	} else if (notif.type === NOTIFICATION_TYPES.WITHDRAWAL_NEW) {
		link += '/admin/withdrawals';
	} else if (notif.type === NOTIFICATION_TYPES.LOGISTIC) {
		// eslint-disable-next-line camelcase
		const receiptNumber = notif?.data?.receipt_number ?? '';
		link += `/logistics/monitoring?receipt_number=${receiptNumber}`;
	}
	return link;
};

// Create Notif
export const createNotif = (title = 'Ostock', options) => {
	const defaultOptions = {
		body: '',
		icon: '',
		vibrate: [100, 50, 100],
		data: {},
	};
	const notifOptions = Object.assign({}, defaultOptions, options);
	if (isMobileOrTablet()) {
		navigator.serviceWorker.getRegistration().then((reg) => {
			reg.showNotification(title, notifOptions);
		});
	} else {
		const link = notifOptions.data.link;
		const onClick = (event) => {
			event.preventDefault(); // prevent the browser from focusing the Notification's tab
			if (link) {
				window.open(link, '_blank');
			}
		};
		const notification = new Notification(title, notifOptions);
		notification.onclick = onClick;
	}
};

// Show Notif
export const showNotif = (title, message, link, icon = 'https://cdn.ostock.id/img/ostock-icon.png') => {
	if (window.Notification) {
		const permission = Notification.permission;
		const options = {
			body: message,
			icon,
			data: {
				link,
			},
			// actions: [
			// 	{ action: 'explore', title: 'Explore this new world', icon: 'https://www.materialui.co/materialIcons/navigation/check_grey_18x18.png' },
			// 	{ action: 'close', title: 'Close notification', icon: 'https://www.materialui.co/materialIcons/navigation/close_grey_18x18.png' },
			// ],
		};
		if (permission === 'granted') {
			createNotif(title, options);
		} else if (permission !== 'denied') {
			Notification.requestPermission().then((permission) => {
				if (permission === 'granted') {
					createNotif(title, options);
				}
			});
		}
	} else {
		// eslint-disable-next-line
		console.log('This browser does not support notification');
	}
};

/**
 * Check if object has a property and the property value not undefined and not null
 *
 * @param {Object} obj Object
 * @param {string} key Property Key
 * @return {Boolean} isNotEmpty
 */
export const isPropNotEmpty = (obj, key) => {
	const isNotEmpty = Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined && obj[key] !== null;
	return isNotEmpty;
};

// Get Prop Value with default
export const getPropValue = (obj, key, defaultValue) => (isPropNotEmpty(obj, key) ? obj[key] : defaultValue);

// Get Value with default
export const getValue = (variable, defaultValue) => (variable !== undefined && variable !== null ? variable : defaultValue);

// Get Random Number
export const getRandomNumber = (base = 10000) => Math.floor(Math.random() * base);

// Get Other Image Size
export const getImageUrl = (imageUrl, size = 'original') => {
	let newUrl = imageUrl;
	const extensions = ['png', 'jpg', 'jpeg'];
	for (let index = 0; index < extensions.length; index++) {
		const extension = extensions[index];
		if (imageUrl.includes(`.${extension}`)) {
			newUrl = imageUrl.replace(`.${extension}`, `-${size}.${extension}`);
			break;
		}
	}
	return newUrl;
};

// De-duplicate Items in Array
export const arrayUnique = (array) => {
	const a = array.concat();
	for (let i = 0; i < a.length; ++i) {
		for (let j = i + 1; j < a.length; ++j) {
			if (a[i] === a[j]) { a.splice(j--, 1); }
		}
	}
	return a;
};

// Filter Input
export const safeTagsReplace = (str, replaceTag = ' ') => str.replace(/[&<>="#;]/g, replaceTag);

// Get Storefront Items for Invoice
export const getStorefrontItems = (orderDetail) => {
	const products = orderDetail.products;
	const cartItems = orderDetail.cart_items;
	const items = [];
	for (let index = 0; index < cartItems.length; index++) {
		const cartItem = cartItems[index];
		const productId = cartItem.product_id.$oid;
		const product = products.find(product => product.product_id.$oid === productId);
		const productName = product ? product.name : '-';
		// const variationName = cartItem.variation ? `${cartItem.variation.replace(/\|\|/g, ' - ')}` : '';
		const quantity = cartItem.qty || '';
		const price = cartItem.price;
		const attributes = product.variations ? product.variations.attributes : [];
		const variations = cartItem.variation ? cartItem.variation.split('||') : [];
		const productMeta = variations.map((variation, index) => {
			const attributeName = attributes[index] ? attributes[index].name : '';
			return `${attributeName}: ${variation}`;
		});
		if (cartItem.notes && cartItem.notes !== '') {
			productMeta.push(`Catatan: ${cartItem.notes}`);
		}
		// Product
		const productItem = {
			name: productName,
			price,
			quantity,
			meta: productMeta,
			type: 'product',
			data: {
				productId,
			},
		};
		items.push(productItem);
		// const cartContent = `- ${productName}${variationName}${qty}: ${price}`;
	}
	return items;
};

// Scroll to hash
export const checkHash = () => {
	setTimeout(() => {
		const section = window.location.hash.substr(1);
		if (section) {
			scrollTo(section);
		}
	}, 300);
};

/**
 * New Line to Paraqraph
 * @param {String} input Content String
 * @see https://jsfiddle.net/CoryDanielson/aYwLg/
 */
export const nl2p = (input) => {
	const contents = input.split('\n');
	const formatted = contents
		// .filter(content => content !== '')
		.join('</p><p>');
	return `<p>${formatted}</p>`;
};

// Insert between text
export const insertBetweenText = (string, text, startPos, endPos) => {
	const output = [text.slice(0, startPos), string, text.slice(endPos)].join('');
	return output;
};

// Shipment Icon
export const getShipmentIcon = (status, courier = 'sicepat') => {
	const icon = shipmentIcons[courier] && shipmentIcons[courier][status]
		? shipmentIcons[courier][status] : 'local_shipping';
	return icon;
};

// Is Tester
export const isTester = userId => testerIds.includes(userId);

// Is Support
export const isSupport = userId => supportIds.includes(userId);

// Is Logistic Support
export const isLogisticSupport = userId => logisticSupportIds.includes(userId);

/**
 * Parse Base64
 * @param {String} imageUrl Image URL
 * @see https://ourcodeworld.com/articles/read/322/how-to-convert-a-base64-image-into-a-image-file-and-upload-it-with-an-asynchronous-form-using-jquery
 */
export const parseBase64 = (imageUrl) => {
	const block = imageUrl.split(';');
	// Get the content type of the image
	const contentType = block[0].split(':')[1];
	// get the real base64 content of the file
	const realData = block[1].split(',')[1];
	return { data: realData, type: contentType };
};

/**
 * Convert a base64 string in a Blob according to the data and contentType.
 *
 * @param {String} b64Data Pure base64 string without contentType
 * @param {String} contentType the content type of the file i.e (image/jpeg - image/png - text/plain)
 * @param {Int} sliceSize SliceSize to process the byteCharacters
 * @see http://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
 * @return Blob
 */
export const b64toBlob = (b64Data, contentType, sliceSize) => {
	contentType = contentType || '';
	sliceSize = sliceSize || 512;

	const byteCharacters = atob(b64Data);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);

		byteArrays.push(byteArray);
	}

	const blob = new Blob(byteArrays, { type: contentType });
	return blob;
};

// Truncate Text
export const truncateText = (text, limit = 10) => {
	if (text.length > limit) {
		for (let i = limit; i > 0; i--) {
			if (text.charAt(i) === ' ' && (text.charAt(i - 1) !== ',' || text.charAt(i - 1) !== '.' || text.charAt(i - 1) !== ';')) {
				return `${text.substring(0, i)}...`;
			}
		}
		return `${text.substring(0, limit)}...`;
	}
	return text;
};

// Is Valid IP Address
export const isValidIp = ipAddress => (/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ipAddress));

/**
 * Convert RGB color code to Hexa color code
 * @param {number} r Red
 * @param {number} g Green
 * @param {number} b Blue
 *
 * @return {string} Hexa Code
 * @see https://snippets.cacher.io/snippet/da116e4c007a9fe533f0
 */
// eslint-disable-next-line no-bitwise
export const rgbToHex = (r, g, b) => `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;

/**
 * Convert Hexa color code to RGB color code
 * @param {string} hex Hexa Color Code (#FFFFFF / #FFF)
 *
 * @return {Object} RGB Color
 * @see https://snippets.cacher.io/snippet/da116e4c007a9fe533f0
 */
export const hexToRgb = (hex) => {
	// Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
	const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
	const replacer = (m, r, g, b) => r + r + g + g + b + b;
	hex = hex.replace(shorthandRegex, replacer);

	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? {
		r: parseInt(result[1], 16),
		g: parseInt(result[2], 16),
		b: parseInt(result[3], 16),
	} : null;
};

/**
 * Get Color Contrast
 * @param {string} hex Hexa Color Code
 */
export const getColorContrast = (hex) => {
	const color = hexToRgb(hex);
	const treshold = 186;
	const contrast = color
		&& (color.r * 0.299 + color.g * 0.587 + color.b * 0.114) > treshold ? 'black' : 'white';
	return contrast;
};

/**
 * Get Text Color
 * @param {string} hex Hexa Color Code
 */
export const getTextColor = (bgColor, lightColor, darkColor) => {
	const contrast = getColorContrast(bgColor);
	return contrast === 'black' ? darkColor : lightColor;
};

// Generate WA URL
export const generateWaUrl = (phone, message) => {
	const isWindowsFirefox = isWindows() && isFirefox();
	const waProtocol = 'whatsapp://send';
	const waUrl = isWindowsFirefox ? 'https://web.whatsapp.com/send' : 'https://api.whatsapp.com/send';
	// const waApiUrl = this.isMobileOrTablet() ? 'whatsapp://send' : waUrl;
	const waApiUrl = isMobileOrTablet() ? waProtocol : waUrl;
	// if (this.isMobileOrTablet() && isChrome()) {
	// 	waApiUrl = waUrl;
	// }
	const text = encodeURIComponent(message);
	const url = `${waApiUrl}?phone=${phone}&text=${text}`;
	return url;
};

// Generate WA URL Web
export const generateWaUrlWeb = (phone, message) => {
	const waUrl = 'https://web.whatsapp.com/send';
	const text = encodeURIComponent(message);
	const url = `${waUrl}?phone=${phone}&text=${text}`;
	return url;
};

// Format WA Number
export const formatWaNumber = (number, countryCode = '62') => {
	let phone = number;
	if (phone.charAt(0) === '+') {
		phone = phone.slice(1, phone.length);
	} else if (phone.charAt(0) === '0') {
		phone = phone.slice(1, phone.length);
		phone = `${countryCode}${phone}`;
	} else if (phone.charAt(0) !== '6' && phone.charAt(1) !== '2') {
		phone = `${countryCode}${phone}`;
	}
	return phone;
};

export const getOS = () => {
	const userAgent = window.navigator.userAgent;
	const platform = window.navigator.platform;
	let os = null;

	if (platform.indexOf('Mac') !== -1) {
		os = 'Mac OS';
	} else if (platform.indexOf('iP') !== -1) {
		os = 'iOS';
	} else if (platform.indexOf('Win') !== -1) {
		os = 'Windows';
	} else if (userAgent.indexOf('Android') !== -1) {
		os = 'Android';
	} else if (!os && platform.indexOf('Linux') !== -1) {
		os = 'Linux';
	}

	return os;
};

export const getBrowser = () => {
	let browser = '';

	// Chrome 1+
	const isChrome = (!!window.chrome && !!window.chrome.webstore) || window.navigator.userAgent.indexOf('Chrome') !== -1;

	// Opera 8.0+
	const isOpera = (!!window.opr && !!window.opr.addons) || !!window.opera || navigator.userAgent.indexOf(' OPR/') >= 0;

	// Firefox 1.0+
	const isFirefox = typeof InstallTrigger !== 'undefined' || window.navigator.userAgent.indexOf('Firefox') !== -1;

	// Safari 3.0+ "[object HTMLElementConstructor]"
	// eslint-disable-next-line func-names
	const isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === '[object SafariRemoteNotification]'; }(!window.safari || (typeof safari !== 'undefined' && window.safari.pushNotification)));

	// Internet Explorer 6-11
	const isIE = /* @cc_on!@ */false || !!document.documentMode;

	// Edge 20+
	const isEdge = !isIE && !!window.StyleMedia;

	// Blink engine detection
	const isBlink = (isChrome || isOpera) && !!window.CSS;

	if (isChrome) {
		browser = 'Chrome';
	} else if (isFirefox) {
		browser = 'Firefox';
	} else if (isOpera) {
		browser = 'Opera';
	} else if (isSafari) {
		browser = 'Safari';
	} else if (isIE) {
		browser = 'IE';
	} else if (isEdge) {
		browser = 'Edge';
	} else if (isBlink) {
		browser = 'Blink';
	}

	return browser;
};

export const getDeviceType = () => (isMobileOrTablet() ? 'mobile' : 'desktop');

export const getLocation = () => '';

export const getUserDeviceLabel = (userDevice) => {
	const location = userDevice.location && userDevice.location !== '' ? userDevice.location : 'Location unknown';
	let deviceType = userDevice.os !== '' ? userDevice.os : 'Device type unknown';
	if (deviceType === 'ios') {
		deviceType = 'iOS';
	}
	return `${deviceType} - ${location}`;
};

export const getUserDeviceIcon = (userDevice) => {
	let icon = 'help';
	const os = userDevice.os;
	const deviceType = userDevice.device;
	if (os === 'Windows' && deviceType === 'desktop') {
		icon = 'desktop_windows';
	} else if (os === 'Android' && deviceType === 'mobile') {
		icon = 'phone_android';
	} else if ((os === 'iPhone' || os === 'iOS' || os === 'ios') && deviceType === 'mobile') {
		icon = 'phone_iphone';
	} else if (os === 'Mac OS' && deviceType === 'desktop') {
		icon = 'desktop_mac';
	}
	return icon;
};

// Get Last Shipment History
export const getLastShipmentHistory = (order) => {
	const courier = order?.shipping?.courier ?? '';
	const histories = order.shipping.trackings && order.shipping.trackings.history
		? order.shipping.trackings.history : [];
	let lastHistory = histories.length > 0 ? histories[histories.length - 1] : null;
	const returnHistory = histories.find(({ status }) => rtsCodes.includes(status));
	if (returnHistory) {
		lastHistory = returnHistory;
	}
	const deliveredStatuses = courierSuccessCodes[courier] ? courierSuccessCodes[courier] : [];
	// const deliveredHistory = histories.find(({ status }) => status === 'DELIVERED');
	const deliveredHistory = histories.find(({ status }) => deliveredStatuses.includes(status));
	if (deliveredHistory) {
		lastHistory = deliveredHistory;
	}
	if (!lastHistory && order.shipping.pickup) {
		// eslint-disable-next-line
		const pickupDateOnly = order?.shipping?.pickup?.pickup_date ?? '';
		// eslint-disable-next-line
		const pickupTime = order?.shipping?.pickup?.pickup_time ?? '';
		const pickupDate = `${pickupDateOnly} ${pickupTime}`;
		lastHistory = {
			date: pickupDate,
			status: 'PICKREQ',
			notes: 'Pickup Request',
		};
	}
	return lastHistory;
};

// Get Courier Label
export const getCourierLabel = (courierKey) => {
	const courier = shippingCouriers.find(({ key }) => key === courierKey);
	const label = courier ? courier.label : courierKey;
	return label;
};

// URL-ify
export const urlify = (text) => {
	// const regex = /((?:ftp|http)s?:\/\/[\w-]+(?:\.[\w-]+)+(?:[\w.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?)/gi;
	const regex = /(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?/gi;
	// here:       ^                                                                                       ^
	// (the non-capturing groups are optional)
	const urlified = text.replace(regex, '<a href="$&" target="_blank" class="exturl">$&</a>');
	return urlified;
};

export const validateEmail = (text) => {
	const mailformat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
	if (text.match(mailformat)) {
		return true;
	}

	return false;
};

export const validatePhone = (phone) => {
	// this.phone.trim() !== '' && !new RegExp(/^\+\d+$/).test(this.phone.trim());
	let valid = false;
	const isNumber = /\d+/g;
	const phoneFormat = phone.match(isNumber);
	if (phoneFormat) {
		valid = true;
	}
	return valid;
};

export const globalResponseHandler = response => response;

export const globalErrorHandler = (error) => {
	const status = error.response.status;
	const isTokenExpired = status === 401;
	if (isTokenExpired) {
		localStorage.removeItem('token');
		const originalRequest = error.config;
		delete originalRequest.headers.Authorization;
		window.location.href = '/';
	}
	return Promise.reject(error);
};

// Create Custom Axios Client
export const createAxiosClient = (url) => {
	const token = localStorage.getItem('token');

	const client = axios.create({
		baseURL: url,
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	// Add a response interceptor
	client.interceptors.response.use(globalResponseHandler, globalErrorHandler);

	return client;
};

// Create Custom Axios Client With No Auth
export const createAxiosClientNoAuth = (url) => {
	const client = axios.create({
		baseURL: url,
		headers: {},
	});

	// Add a response interceptor
	client.interceptors.response.use(globalResponseHandler, globalErrorHandler);

	return client;
};

export const generateRandomString = (length = 5) => {
	// vaiable regex random string
	const rndString = Math.random()
		.toString(36)
		.replace(/[^a-z]+/g, '')
		.substr(0, length);

	return rndString;
};

// Get Payment Channel label
export const getPaymentChannelLabel = (channel) => {
	const paymentChannel = paymentChannels.find(({ key }) => key === channel);
	return paymentChannel ? paymentChannel.label : toTitleCase(channel.replace(/_/g, ' '));
};

// Get Payment Info Label
export const getPaymentInfo = (submission) => {
	const paymentDescription = submission.payment && submission.payment.data && submission.payment.data.bank_type
		? `${submission.payment.data.bank_type.toUpperCase()} ${submission.payment.data.account_number}` : false;
	const paymentMethodCode = submission.payment.method ? submission.payment.method : 'bank_transfer';
	const paymentMethod = toTitleCase(paymentMethodCode.replace(/_/g, ' '));
	let paymentInfo = paymentDescription ? `${paymentMethod} - ${paymentDescription}` : paymentMethod;
	if (paymentMethodCode === 'instant') {
		const channels = submission?.payment?.channels ?? [];
		if (channels.length > 0) {
			const channel = channels[0];
			const channelLabel = getPaymentChannelLabel(channel);
			paymentInfo = `E-Payment - ${channelLabel}`;
		}
	}
	return paymentInfo;
};

// Get Shipping Cost Diff
export const getShippingCostDiff = (order, courier = '-', type = 'shipping_fee') => {
	// const courier = order.shipping && order.shipping.courier ? order.shipping.courier : '-';
	const cost = order.shipping && order.shipping.original_cost
		? order.shipping.original_cost : order.shipping.cost;
	const courierCost = order.courier_shipping_cost || 0;
	let discount = 0.3; // 30% discount
	let discountValue = 30;
	if (type === 'shipping_fee' || type === 'cod_fee') {
		if (courier === 'sicepat') {
			// discount = 35 / 100;
			discountValue = courierDiscount.sicepat;
		} if (courier === 'jne') {
			// discount = 30 / 100;
			discountValue = courierDiscount.jne;
		} if (courier === 'sap') {
			// discount = 30 / 100;
			discountValue = courierDiscount.sap;
		} else if (courier === 'jnt' || courier === 'J&T') {
			// discount = 25 / 100;
			discountValue = courierDiscount.jnt;
		}
	} else if (type === 'return_fee') {
		if (courier === 'sicepat') {
			discountValue = courierReturnDiscount.sicepat;
		} if (courier === 'jne') {
			discountValue = courierReturnDiscount.jne;
		} if (courier === 'sap') {
			discountValue = courierReturnDiscount.sap;
		} else if (courier === 'jnt' || courier === 'J&T') {
			discountValue = courierReturnDiscount.jnt;
		}
	}
	discount = discountValue / 100;
	const finalCost = cost - (cost * discount);
	const finalCourierCost = courierCost - (courierCost * discount);
	const value = finalCost - finalCourierCost;
	return value;
};

export const courierServicesItems = {
	regular: ['REG', 'Paket Kilat Khusus', 'EZ', 'DES', 'STANDARD', 'RGP', 'REGPACK', 'UDRREG', 'SIUNT', 'CTC'],
	next_day: ['YES', 'ONS', 'BEST', 'Express Next Day Barang', 'NEXTDAY', 'NDP', 'ONEPACK', 'ODS', 'UDRONS', 'CTCYES'],
	economical: ['OKE', 'ECO', 'Paketpos Biasa', 'Normal', 'RGS'],
	same_day: ['Q9 Barang'],
	truck: ['JTR', 'JTR250', 'JTR<150', 'JTR>250', 'Jumbo Ekonomi'],
};

export const setServicesItems = (services) => {
	let newServices = [];
	const shippingServices = services.map((service) => {
		const availableServices = courierServicesItems[service] || [];
		return availableServices;
	});

	for (let i = 0; i < shippingServices.length; i++) {
		newServices = newServices.concat(shippingServices[i]);
	}

	return newServices;
};

export const setProductVariationName = (variations = null) => {
	let name = '';
	if (variations !== null) {
		const variation = Object.keys(variations).map((key) => {
			const items = variations[key];
			return items;
		});
		name = variation.join('||');
	}
	return name;
};

export const getIsDiscounted = (product) => {
	let discounted = false;

	// product simple
	if (product && product.type && product.type === 'simple') {
		// discounted = product.sale_price.enabled && isDiscountDateValid(product.sale_price.schedule);
		discounted = product.sale_price.enabled;
	}
	return discounted;
};

export const getSalePrice = (product) => {
	let price = 0;
	price = product.sale_price.price;
	return price;
};

/**
 *
 * @param {*} shipping {} Shipping Config
 * @param {*} productPrice
 * @param {*} shippingCost
 */
export const getCODCost = (shipping = null, productPrice, shippingCost) => {
	let cost = 0;
	if (shipping !== null) {
		const shippingCOD = shipping;
		const costPercent = shippingCOD.percentage / 100;
		const minimalCost = shippingCOD.minimal;
		const maximalCost = shippingCOD.maximal;

		if (shippingCOD.feeType === 'product_shipping') {
			cost = costPercent * (productPrice + shippingCost);

			// eslint-disable-next-line
			if (cost <= minimalCost) {
				cost = minimalCost;
			}

			if (cost >= maximalCost) {
				cost = maximalCost;
			}

		// eslint-disable-next-line
		} else if (shippingCOD.feeType === 'product') {
			cost = costPercent * productPrice;

			// eslint-disable-next-lin
			if (cost <= minimalCost) {
				cost = minimalCost;
			}

			if (cost >= maximalCost) {
				cost = maximalCost;
			}
		}
	}

	return cost;
};

export const getFinalPrice = (product, selectedProductVariation = [], quantity = 1) => {
	// console.log('selectedProductVariation: ', selectedProductVariation);
	// console.log('product: ', product);
	const isMultipleVariation = product.variations.multiple_variations.enabled;
	let price = 0;
	// type variable
	if (product.type === 'variable') {
		// mutliple variation
		if (isMultipleVariation) {
			if (selectedProductVariation.length > 0) {
				const mutliplePrices = [];
				for (let i = 0; i < selectedProductVariation.length; i++) {
					const element = selectedProductVariation[i];
					let variantPrice = element.variant?.price;
					const qty = element.quantity;
					const wholesale = element.variant?.wholesale;
					variantPrice = getWholesalePrice(wholesale, qty, variantPrice);
					variantPrice *= qty;
					mutliplePrices.push(variantPrice);
				}
				price = mutliplePrices.reduce((a, b) => a + b, 0);
			}
		// not multiple variations
		} else {
			const name = setProductVariationName(selectedProductVariation);
			const productVariationsPrices = product.variations ? product.variations.prices : [];
			const variation = productVariationsPrices.find(curr => curr.name === name);
			if (variation) {
				const variationPrice = variation.price;
				const variationWholesale = variation.wholesale;
				price = getWholesalePrice(variationWholesale, quantity, variationPrice);
				price *= quantity;
			} else if (product.variation_price) {
				price = product.variation_price;
				price *= quantity;
			}
		}
	// type simple
	} else {
		const isOnDiscount = getIsDiscounted(product);
		const salePrice = getSalePrice(product);
		const wholesales = product.wholesale || [];
		const normalPrice = isOnDiscount ? salePrice : product.price;
		price = getWholesalePrice(wholesales, quantity, normalPrice);
		price *= quantity;
	}
	return price;
};

export const formattedMultipleVariantAttributeName = (attributes = [], value) => {
	const nameArray = value.split('||');
	const attributeName = attributes.map(curr => curr.name);
	const finalName = nameArray.map((curr, index) => {
		curr = `${attributeName[index]}: ${curr}`;
		return curr;
	});
	return finalName.join(', ');
};

// eslint-disable-next-line
export const createSelectedVariation = (variationPrices = [], variationAttributes = [], stockEnabled = false) => {
	// validate variation
	const validVariation = variationPrices.find(curr => stockEnabled && curr.stock.quantity > 0) || variationPrices[0];

	// split name and change to array
	const splittedVariations = validVariation.name.split('||');

	// create object
	const selectedVariation = {};

	for (let indexSplitted = 0; indexSplitted < splittedVariations.length; indexSplitted++) {
		const attributeValue = splittedVariations[indexSplitted];
		const attributeName = variationAttributes[indexSplitted] ? variationAttributes[indexSplitted].name : '';
		if (attributeName !== '') {
			selectedVariation[attributeName] = attributeValue;
		}
	}

	return selectedVariation;
};

export const displayVariation = variation => (variation ? variation.replace(/\|\|/g, ' - ') : '');

export const phoneNumberFormatting = (phoneNumber, countryCode = 'ID') => {
	// eslint-disable-next-line
	if (countryCode == 'ID') {
		phoneNumber = phoneNumber.replace(/^8/, '08');
		phoneNumber = phoneNumber.replace(/^2/, '02');
		phoneNumber = phoneNumber.replace(/^0|^62/, '+62');
	}

	return phoneNumber;
};

// export const getIconStyle = (iconStyles = []) => {
// 	const styles = [
// 		{
// 			key: 'brands',
// 			style: 'fab',
// 		},
// 		{
// 			key: 'regular',
// 			style: 'far',
// 		},
// 		{
// 			key: 'solid',
// 			style: 'fas',
// 		},
// 	];
// 	const style = styles.find(curr => iconStyles.includes(curr.key)).style;
// 	// console.log('style: ', style);
// 	return style;
// };

export const getIconStyle = (iconStyle) => {
	let icon = iconStyle;
	if (iconStyle === 'fas') {
		icon = 'solid';
	} else if (iconStyle === 'far') {
		icon = 'regular';
	} else if (iconStyle === 'fab') {
		icon = 'brands';
	}
	const styles = [
		{
			key: 'brands',
			style: 'fab',
		},
		{
			key: 'regular',
			style: 'far',
		},
		{
			key: 'solid',
			style: 'fas',
		},
	];
	let style = '';
	if (Array.isArray(iconStyle)) {
		const theStyle = styles.find(curr => iconStyle.includes(curr.key));
		if (theStyle) {
			style = theStyle.style;
		}
	} else {
		const theStyle = styles.find(curr => icon === curr.key);
		if (theStyle) {
			style = theStyle.style;
		}
	}
	return style;
};

export const getRgbaColor = (rgba = null) => {
	const color = rgba !== null
		? `rgba(${rgba.r} ${rgba.g} ${rgba.b} / ${rgba.a})`
		: 'rgb(255 255 255 / 11%)';
	return color;
};

export const getIcon = (iconKey, iconStyle) => {
	let icon = '';
	let style = iconStyle;
	if (iconKey !== '' && iconStyle !== '') {
		if (iconStyle !== 'fab' && iconStyle !== 'far' && iconStyle !== 'fas') {
			style = getIconStyle(iconStyle);
		}
		icon = `${style} fa-${iconKey}`;
	}
	return icon;
};

export const removeHtmlTags = str => str.replace(/<[^>]*>?/gm, '');

/**
 *
 * @param {*} options
 * @param {*} styles ['text-color', 'font-family', 'font-weight', 'font-size', 'font-style', 'text-decoration', 'line-height', 'padding', 'margin', 'border', 'bg-color', 'bg-img', 'box-shadow', 'button']
 */
export const getNodeStyle = (options, styles = [], device = 'desktop') => {
	const style = {};

	// text color
	if (styles.includes('text-color')) {
		style.color = options.textColor && options.textColor.rgb && options.textColor.rgb !== ''
			? getRgbaColor(options.textColor.rgb)
			: options.textColor;
	}

	// title color
	if (styles.includes('title-color')) {
		style.color = options.titleTextColor && options.titleTextColor.rgb && options.titleTextColor.rgb !== ''
			? getRgbaColor(options.titleTextColor.rgb)
			: options.titleTextColor;
	}

	// sub title color
	if (styles.includes('subtitle-color')) {
		style.color = options.subTitleTextColor && options.subTitleTextColor.rgb && options.subTitleTextColor.rgb !== ''
			? getRgbaColor(options.subTitleTextColor.rgb)
			: options.subTitleTextColor;
	}

	// price color
	if (styles.includes('price-color')) {
		style.color = options.priceTextColor && options.priceTextColor.rgb && options.priceTextColor.rgb !== ''
			? getRgbaColor(options.priceTextColor.rgb)
			: options.priceTextColor;
	}

	// feature label color
	if (styles.includes('feature-label-color')) {
		style.color = options.featureLabelTextColor && options.featureLabelTextColor.rgb && options.featureLabelTextColor.rgb !== ''
			? getRgbaColor(options.featureLabelTextColor.rgb)
			: options.featureLabelTextColor;
	}

	// feature color
	if (styles.includes('feature-color')) {
		style.color = options.featureTextColor && options.featureTextColor.rgb && options.featureTextColor.rgb !== ''
			? getRgbaColor(options.featureTextColor.rgb)
			: options.featureTextColor;
	}

	// title active color
	if (styles.includes('title-active-color')) {
		style.color = options.titleActiveTextColor && options.titleActiveTextColor.rgb && options.titleActiveTextColor.rgb !== ''
			? getRgbaColor(options.titleActiveTextColor.rgb)
			: options.titleActiveTextColor;
	}

	// content color
	if (styles.includes('content-color')) {
		style.color = options.contentTextColor && options.contentTextColor.rgb && options.contentTextColor.rgb !== ''
			? getRgbaColor(options.contentTextColor.rgb)
			: options.contentTextColor;
	}

	// button text color
	if (styles.includes('button-color')) {
		style.color = options.buttonTextColor && options.buttonTextColor.rgb && options.buttonTextColor.rgb !== ''
			? getRgbaColor(options.buttonTextColor.rgb)
			: options.buttonTextColor;
	}

	// font size
	if (styles.includes('font-size')) {
		if (device === 'desktop') {
			const fixedFontSizeUnit = options.fontSizeUnit && options.fontSizeUnit !== '' ? options.fontSizeUnit : 'px';
			style['font-size'] = options.fontSize && options.fontSize !== ''
				? `${options.fontSize}${fixedFontSizeUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedMobileFontSizeUnit = options.mobileFontSizeUnit && options.mobileFontSizeUnit !== '' ? options.mobileFontSizeUnit : 'px';
			style['font-size'] = options.mobileFontSize && options.mobileFontSize !== ''
				? `${options.mobileFontSize}${fixedMobileFontSizeUnit}`
				: '';
		}
	}

	// title font size
	if (styles.includes('title-font-size')) {
		if (device === 'desktop') {
			const fixedFontSizeUnit = options.titleFontSizeUnit && options.titleFontSizeUnit !== '' ? options.titleFontSizeUnit : 'px';
			style['font-size'] = options.titleFontSize && options.titleFontSize !== ''
				? `${options.titleFontSize}${fixedFontSizeUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedMobileFontSizeUnit = options.mobileTitleFontSizeUnit && options.mobileTitleFontSizeUnit !== '' ? options.mobileTitleFontSizeUnit : 'px';
			style['font-size'] = options.mobileTitleFontSize && options.mobileTitleFontSize !== ''
				? `${options.mobileTitleFontSize}${fixedMobileFontSizeUnit}`
				: '';
		}
	}

	// subtitle font size
	if (styles.includes('subtitle-font-size')) {
		if (device === 'desktop') {
			const fixedFontSizeUnit = options.subTitleFontSizeUnit && options.subTitleFontSizeUnit !== '' ? options.subTitleFontSizeUnit : 'px';
			style['font-size'] = options.subTitleFontSize && options.subTitleFontSize !== ''
				? `${options.subTitleFontSize}${fixedFontSizeUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedMobileFontSizeUnit = options.mobileSubTitleFontSizeUnit && options.mobileSubTitleFontSizeUnit !== '' ? options.mobileSubTitleFontSizeUnit : 'px';
			style['font-size'] = options.mobileSubTitleFontSize && options.mobileSubTitleFontSize !== ''
				? `${options.mobileSubTitleFontSize}${fixedMobileFontSizeUnit}`
				: '';
		}
	}

	// feature label font size
	if (styles.includes('feature-label-font-size')) {
		if (device === 'desktop') {
			const fixedFontSizeUnit = options.featureLabelFontSizeUnit && options.featureLabelFontSizeUnit !== '' ? options.featureLabelFontSizeUnit : 'px';
			style['font-size'] = options.featureLabelFontSize && options.featureLabelFontSize !== ''
				? `${options.featureLabelFontSize}${fixedFontSizeUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedMobileFontSizeUnit = options.mobileFeatureLabelFontSizeUnit && options.mobileFeatureLabelFontSizeUnit !== '' ? options.mobileFeatureLabelFontSizeUnit : 'px';
			style['font-size'] = options.mobileFeatureLabelFontSize && options.mobileFeatureLabelFontSize !== ''
				? `${options.mobileFeatureLabelFontSize}${fixedMobileFontSizeUnit}`
				: '';
		}
	}

	// feature font size
	if (styles.includes('feature-font-size')) {
		if (device === 'desktop') {
			const fixedFontSizeUnit = options.featureFontSizeUnit && options.featureFontSizeUnit !== '' ? options.featureFontSizeUnit : 'px';
			style['font-size'] = options.featureFontSize && options.featureFontSize !== ''
				? `${options.featureFontSize}${fixedFontSizeUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedMobileFontSizeUnit = options.mobileFeatureFontSizeUnit && options.mobileFeatureFontSizeUnit !== '' ? options.mobileFeatureFontSizeUnit : 'px';
			style['font-size'] = options.mobileFeatureFontSize && options.mobileFeatureFontSize !== ''
				? `${options.mobileFeatureFontSize}${fixedMobileFontSizeUnit}`
				: '';
		}
	}

	// price font size
	if (styles.includes('price-font-size')) {
		if (device === 'desktop') {
			const fixedFontSizeUnit = options.priceFontSizeUnit && options.priceFontSizeUnit !== '' ? options.priceFontSizeUnit : 'px';
			style['font-size'] = options.priceFontSize && options.priceFontSize !== ''
				? `${options.priceFontSize}${fixedFontSizeUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedMobileFontSizeUnit = options.mobilePriceFontSizeUnit && options.mobilePriceFontSizeUnit !== '' ? options.mobilePriceFontSizeUnit : 'px';
			style['font-size'] = options.mobilePriceFontSize && options.mobilePriceFontSize !== ''
				? `${options.mobilePriceFontSize}${fixedMobileFontSizeUnit}`
				: '';
		}
	}

	// content font size
	if (styles.includes('content-font-size')) {
		if (device === 'desktop') {
			const fixedFontSizeUnit = options.contentFontSizeUnit && options.contentFontSizeUnit !== '' ? options.contentFontSizeUnit : 'px';
			style['font-size'] = options.contentFontSize && options.contentFontSize !== ''
				? `${options.contentFontSize}${fixedFontSizeUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedMobileFontSizeUnit = options.mobileContentFontSizeUnit && options.mobileContentFontSizeUnit !== '' ? options.mobileContentFontSizeUnit : 'px';
			style['font-size'] = options.mobileContentFontSize && options.mobileContentFontSize !== ''
				? `${options.mobileContentFontSize}${fixedMobileFontSizeUnit}`
				: '';
		}
	}

	// button font size
	if (styles.includes('button-font-size')) {
		if (device === 'desktop') {
			const fixedFontSizeUnit = options.buttonFontSizeUnit && options.buttonFontSizeUnit !== '' ? options.buttonFontSizeUnit : 'px';
			style['font-size'] = options.buttonFontSize && options.buttonFontSize !== ''
				? `${options.buttonFontSize}${fixedFontSizeUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedMobileFontSizeUnit = options.mobileButtonFontSizeUnit && options.mobileButtonFontSizeUnit !== '' ? options.mobileButtonFontSizeUnit : 'px';
			style['font-size'] = options.mobileButtonFontSize && options.mobileButtonFontSize !== ''
				? `${options.mobileButtonFontSize}${fixedMobileFontSizeUnit}`
				: '';
		}
	}

	// font family
	if (styles.includes('font-family')) {
		if (options.fontFamily && options.fontFamily !== '') {
			style['font-family'] = `${options.fontFamily}, sans-serif` || '';
		}
	}

	// title font family
	if (styles.includes('title-font-family')) {
		if (options.titleFontFamily && options.titleFontFamily !== '') {
			style['font-family'] = `${options.titleFontFamily}, sans-serif` || '';
		}
	}

	// sub title font family
	if (styles.includes('subtitle-font-family')) {
		if (options.subTitleFontFamily && options.subTitleFontFamily !== '') {
			style['font-family'] = `${options.subTitleFontFamily}, sans-serif` || '';
		}
	}

	// content font family
	if (styles.includes('content-font-family')) {
		if (options.contentFontFamily && options.contentFontFamily !== '') {
			style['font-family'] = `${options.contentFontFamily}, sans-serif` || '';
		}
	}

	// feature label font family
	if (styles.includes('feature-label-font-family')) {
		if (options.featureLabelFontFamily && options.featureLabelFontFamily !== '') {
			style['font-family'] = `${options.featureLabelFontFamily}, sans-serif` || '';
		}
	}

	// feature font family
	if (styles.includes('feature-font-family')) {
		if (options.featureFontFamily && options.featureFontFamily !== '') {
			style['font-family'] = `${options.featureFontFamily}, sans-serif` || '';
		}
	}

	// price font family
	if (styles.includes('price-font-family')) {
		if (options.priceFontFamily && options.priceFontFamily !== '') {
			style['font-family'] = `${options.priceFontFamily}, sans-serif` || '';
		}
	}

	// button font family
	if (styles.includes('button-font-family')) {
		if (options.buttonFontFamily && options.buttonFontFamily !== '') {
			style['font-family'] = `${options.buttonFontFamily}, sans-serif` || '';
		}
	}

	// font weight
	if (styles.includes('font-weight')) {
		style['font-weight'] = options.fontWeight || '';
	}

	// title font weight
	if (styles.includes('title-font-weight')) {
		style['font-weight'] = options.titleFontWeight || '';
	}

	// title font weight
	if (styles.includes('subtitle-font-weight')) {
		style['font-weight'] = options.subTitleFontWeight || '';
	}

	// content font weight
	if (styles.includes('content-font-weight')) {
		style['font-weight'] = options.contentFontWeight || '';
	}

	// button font weight
	if (styles.includes('button-font-weight')) {
		style['font-weight'] = options.buttonFontWeight || '';
	}

	// text style italic
	if (styles.includes('font-style')) {
		style['font-style'] = options.textItalic ? 'italic' : '';
	}

	// text decoration
	if (styles.includes('text-decoration')) {
		style['text-decoration'] = options.textUnderline ? 'underline' : '';
	}

	// line height
	if (styles.includes('line-height')) {
		const fixedLineHeightUnit = options.lineHeightUnit && options.lineHeightUnit !== '' ? options.lineHeightUnit : 'px';
		style['line-height'] = options.lineHeight && options.lineHeight !== ''
			? `${options.lineHeight}${fixedLineHeightUnit}`
			: '';
	}

	// title line height
	if (styles.includes('title-line-height')) {
		const fixedLineHeightUnit = options.titleLineHeightUnit && options.titleLineHeightUnit !== '' ? options.titleLineHeightUnit : 'px';
		style['line-height'] = options.titleLineHeight && options.titleLineHeight !== ''
			? `${options.titleLineHeight}${fixedLineHeightUnit}`
			: '';
	}

	// sub title line height
	if (styles.includes('subtitle-line-height')) {
		const fixedLineHeightUnit = options.subTitleLineHeightUnit && options.subTitleLineHeightUnit !== '' ? options.subTitleLineHeightUnit : 'px';
		style['line-height'] = options.subTitleLineHeight && options.subTitleLineHeight !== ''
			? `${options.subTitleLineHeight}${fixedLineHeightUnit}`
			: '';
	}

	// content line height
	if (styles.includes('content-line-height')) {
		const fixedLineHeightUnit = options.contentLineHeightUnit && options.contentLineHeightUnit !== '' ? options.contentLineHeightUnit : 'px';
		style['line-height'] = options.contentLineHeight && options.contentLineHeight !== ''
			? `${options.contentLineHeight}${fixedLineHeightUnit}`
			: '';
	}

	// button line height
	if (styles.includes('button-line-height')) {
		const fixedLineHeightUnit = options.buttonLineHeightUnit && options.buttonLineHeightUnit !== '' ? options.buttonLineHeightUnit : 'px';
		style['line-height'] = options.buttonLineHeight && options.buttonLineHeight !== ''
			? `${options.buttonLineHeight}${fixedLineHeightUnit}`
			: '';
	}

	// padding
	if (styles.includes('padding')) {
		let paddingUnit = 'px';
		let paddingTop = 1;
		let paddingRight = 1;
		let paddingBottom = 1;
		let paddingLeft = 1;

		if (device === 'desktop') {
			paddingUnit = options.paddingUnit && options.paddingUnit !== '' ? options.paddingUnit : 'px';
			paddingTop = options.paddingTop && options.paddingTop !== '' ? options.paddingTop : '';
			paddingRight = options.paddingRight && options.paddingRight !== '' ? options.paddingRight : '';
			paddingBottom = options.paddingBottom && options.paddingBottom !== '' ? options.paddingBottom : '';
			paddingLeft = options.paddingLeft && options.paddingLeft !== '' ? options.paddingLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			paddingUnit = options.mobilePaddingUnit && options.mobilePaddingUnit !== '' ? options.mobilePaddingUnit : 'px';
			paddingTop = options.mobilePaddingTop && options.mobilePaddingTop !== '' ? options.mobilePaddingTop : '';
			paddingRight = options.mobilePaddingRight && options.mobilePaddingRight !== '' ? options.mobilePaddingRight : '';
			paddingBottom = options.mobilePaddingBottom && options.mobilePaddingBottom !== '' ? options.mobilePaddingBottom : '';
			paddingLeft = options.mobilePaddingLeft && options.mobilePaddingLeft !== '' ? options.mobilePaddingLeft : '';
		}

		style['padding-top'] = `${paddingTop}${paddingUnit}`;
		style['padding-right'] = `${paddingRight}${paddingUnit}`;
		style['padding-bottom'] = `${paddingBottom}${paddingUnit}`;
		style['padding-left'] = `${paddingLeft}${paddingUnit}`;
	}

	// title padding
	if (styles.includes('title-padding')) {
		let paddingUnit = 'px';
		let paddingTop = 1;
		let paddingRight = 1;
		let paddingBottom = 1;
		let paddingLeft = 1;

		if (device === 'desktop') {
			paddingUnit = options.titlePaddingUnit && options.titlePaddingUnit !== '' ? options.titlePaddingUnit : 'px';
			paddingTop = options.titlePaddingTop && options.titlePaddingTop !== '' ? options.titlePaddingTop : '';
			paddingRight = options.titlePaddingRight && options.titlePaddingRight !== '' ? options.titlePaddingRight : '';
			paddingBottom = options.titlePaddingBottom && options.titlePaddingBottom !== '' ? options.titlePaddingBottom : '';
			paddingLeft = options.titlePaddingLeft && options.titlePaddingLeft !== '' ? options.titlePaddingLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			paddingUnit = options.mobileTitlePaddingUnit && options.mobileTitlePaddingUnit !== '' ? options.mobileTitlePaddingUnit : 'px';
			paddingTop = options.mobileTitlePaddingTop && options.mobileTitlePaddingTop !== '' ? options.mobileTitlePaddingTop : '';
			paddingRight = options.mobileTitlePaddingRight && options.mobileTitlePaddingRight !== '' ? options.mobileTitlePaddingRight : '';
			paddingBottom = options.mobileTitlePaddingBottom && options.mobileTitlePaddingBottom !== '' ? options.mobileTitlePaddingBottom : '';
			paddingLeft = options.mobileTitlePaddingLeft && options.mobileTitlePaddingLeft !== '' ? options.mobileTitlePaddingLeft : '';
		}

		style['padding-top'] = `${paddingTop}${paddingUnit}`;
		style['padding-right'] = `${paddingRight}${paddingUnit}`;
		style['padding-bottom'] = `${paddingBottom}${paddingUnit}`;
		style['padding-left'] = `${paddingLeft}${paddingUnit}`;
	}

	// sub title padding
	if (styles.includes('subtitle-padding')) {
		let paddingUnit = 'px';
		let paddingTop = 1;
		let paddingRight = 1;
		let paddingBottom = 1;
		let paddingLeft = 1;

		if (device === 'desktop') {
			paddingUnit = options.subTitlePaddingUnit && options.subTitlePaddingUnit !== '' ? options.subTitlePaddingUnit : 'px';
			paddingTop = options.subTitlePaddingTop && options.subTitlePaddingTop !== '' ? options.subTitlePaddingTop : '';
			paddingRight = options.subTitlePaddingRight && options.subTitlePaddingRight !== '' ? options.subTitlePaddingRight : '';
			paddingBottom = options.subTitlePaddingBottom && options.subTitlePaddingBottom !== '' ? options.subTitlePaddingBottom : '';
			paddingLeft = options.subTitlePaddingLeft && options.subTitlePaddingLeft !== '' ? options.subTitlePaddingLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			paddingUnit = options.mobileSubTitlePaddingUnit && options.mobileSubTitlePaddingUnit !== '' ? options.mobileSubTitlePaddingUnit : 'px';
			paddingTop = options.mobileSubTitlePaddingTop && options.mobileSubTitlePaddingTop !== '' ? options.mobileSubTitlePaddingTop : '';
			paddingRight = options.mobileSubTitlePaddingRight && options.mobileSubTitlePaddingRight !== '' ? options.mobileSubTitlePaddingRight : '';
			paddingBottom = options.mobileSubTitlePaddingBottom && options.mobileSubTitlePaddingBottom !== '' ? options.mobileSubTitlePaddingBottom : '';
			paddingLeft = options.mobileSubTitlePaddingLeft && options.mobileSubTitlePaddingLeft !== '' ? options.mobileSubTitlePaddingLeft : '';
		}

		style['padding-top'] = `${paddingTop}${paddingUnit}`;
		style['padding-right'] = `${paddingRight}${paddingUnit}`;
		style['padding-bottom'] = `${paddingBottom}${paddingUnit}`;
		style['padding-left'] = `${paddingLeft}${paddingUnit}`;
	}

	// price padding
	if (styles.includes('price-padding')) {
		let paddingUnit = 'px';
		let paddingTop = 1;
		let paddingRight = 1;
		let paddingBottom = 1;
		let paddingLeft = 1;

		if (device === 'desktop') {
			paddingUnit = options.pricePaddingUnit && options.pricePaddingUnit !== '' ? options.pricePaddingUnit : 'px';
			paddingTop = options.pricePaddingTop && options.pricePaddingTop !== '' ? options.pricePaddingTop : '';
			paddingRight = options.pricePaddingRight && options.pricePaddingRight !== '' ? options.pricePaddingRight : '';
			paddingBottom = options.pricePaddingBottom && options.pricePaddingBottom !== '' ? options.pricePaddingBottom : '';
			paddingLeft = options.pricePaddingLeft && options.pricePaddingLeft !== '' ? options.pricePaddingLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			paddingUnit = options.mobilePricePaddingUnit && options.mobilePricePaddingUnit !== '' ? options.mobilePricePaddingUnit : 'px';
			paddingTop = options.mobilePricePaddingTop && options.mobilePricePaddingTop !== '' ? options.mobilePricePaddingTop : '';
			paddingRight = options.mobilePricePaddingRight && options.mobilePricePaddingRight !== '' ? options.mobilePricePaddingRight : '';
			paddingBottom = options.mobilePricePaddingBottom && options.mobilePricePaddingBottom !== '' ? options.mobilePricePaddingBottom : '';
			paddingLeft = options.mobilePricePaddingLeft && options.mobilePricePaddingLeft !== '' ? options.mobilePricePaddingLeft : '';
		}

		style['padding-top'] = `${paddingTop}${paddingUnit}`;
		style['padding-right'] = `${paddingRight}${paddingUnit}`;
		style['padding-bottom'] = `${paddingBottom}${paddingUnit}`;
		style['padding-left'] = `${paddingLeft}${paddingUnit}`;
	}

	// featureLabel padding
	if (styles.includes('feature-label-padding')) {
		let paddingUnit = 'px';
		let paddingTop = 1;
		let paddingRight = 1;
		let paddingBottom = 1;
		let paddingLeft = 1;

		if (device === 'desktop') {
			paddingUnit = options.featureLabelPaddingUnit && options.featureLabelPaddingUnit !== '' ? options.featureLabelPaddingUnit : 'px';
			paddingTop = options.featureLabelPaddingTop && options.featureLabelPaddingTop !== '' ? options.featureLabelPaddingTop : '';
			paddingRight = options.featureLabelPaddingRight && options.featureLabelPaddingRight !== '' ? options.featureLabelPaddingRight : '';
			paddingBottom = options.featureLabelPaddingBottom && options.featureLabelPaddingBottom !== '' ? options.featureLabelPaddingBottom : '';
			paddingLeft = options.featureLabelPaddingLeft && options.featureLabelPaddingLeft !== '' ? options.featureLabelPaddingLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			paddingUnit = options.mobileFeatureLabelPaddingUnit && options.mobileFeatureLabelPaddingUnit !== '' ? options.mobileFeatureLabelPaddingUnit : 'px';
			paddingTop = options.mobileFeatureLabelPaddingTop && options.mobileFeatureLabelPaddingTop !== '' ? options.mobileFeatureLabelPaddingTop : '';
			paddingRight = options.mobileFeatureLabelPaddingRight && options.mobileFeatureLabelPaddingRight !== '' ? options.mobileFeatureLabelPaddingRight : '';
			paddingBottom = options.mobileFeatureLabelPaddingBottom && options.mobileFeatureLabelPaddingBottom !== '' ? options.mobileFeatureLabelPaddingBottom : '';
			paddingLeft = options.mobileFeatureLabelPaddingLeft && options.mobileFeatureLabelPaddingLeft !== '' ? options.mobileFeatureLabelPaddingLeft : '';
		}

		style['padding-top'] = `${paddingTop}${paddingUnit}`;
		style['padding-right'] = `${paddingRight}${paddingUnit}`;
		style['padding-bottom'] = `${paddingBottom}${paddingUnit}`;
		style['padding-left'] = `${paddingLeft}${paddingUnit}`;
	}

	// feature padding
	if (styles.includes('feature-padding')) {
		let paddingUnit = 'px';
		let paddingTop = 1;
		let paddingRight = 1;
		let paddingBottom = 1;
		let paddingLeft = 1;

		if (device === 'desktop') {
			paddingUnit = options.featurePaddingUnit && options.featurePaddingUnit !== '' ? options.featurePaddingUnit : 'px';
			paddingTop = options.featurePaddingTop && options.featurePaddingTop !== '' ? options.featurePaddingTop : '';
			paddingRight = options.featurePaddingRight && options.featurePaddingRight !== '' ? options.featurePaddingRight : '';
			paddingBottom = options.featurePaddingBottom && options.featurePaddingBottom !== '' ? options.featurePaddingBottom : '';
			paddingLeft = options.featurePaddingLeft && options.featurePaddingLeft !== '' ? options.featurePaddingLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			paddingUnit = options.mobileFeaturePaddingUnit && options.mobileFeaturePaddingUnit !== '' ? options.mobileFeaturePaddingUnit : 'px';
			paddingTop = options.mobileFeaturePaddingTop && options.mobileFeaturePaddingTop !== '' ? options.mobileFeaturePaddingTop : '';
			paddingRight = options.mobileFeaturePaddingRight && options.mobileFeaturePaddingRight !== '' ? options.mobileFeaturePaddingRight : '';
			paddingBottom = options.mobileFeaturePaddingBottom && options.mobileFeaturePaddingBottom !== '' ? options.mobileFeaturePaddingBottom : '';
			paddingLeft = options.mobileFeaturePaddingLeft && options.mobileFeaturePaddingLeft !== '' ? options.mobileFeaturePaddingLeft : '';
		}

		style['padding-top'] = `${paddingTop}${paddingUnit}`;
		style['padding-right'] = `${paddingRight}${paddingUnit}`;
		style['padding-bottom'] = `${paddingBottom}${paddingUnit}`;
		style['padding-left'] = `${paddingLeft}${paddingUnit}`;
	}

	// content padding
	if (styles.includes('content-padding')) {
		let paddingUnit = 'px';
		let paddingTop = 1;
		let paddingRight = 1;
		let paddingBottom = 1;
		let paddingLeft = 1;

		if (device === 'desktop') {
			paddingUnit = options.contentPaddingUnit && options.contentPaddingUnit !== '' ? options.contentPaddingUnit : 'px';
			paddingTop = options.contentPaddingTop && options.contentPaddingTop !== '' ? options.contentPaddingTop : '';
			paddingRight = options.contentPaddingRight && options.contentPaddingRight !== '' ? options.contentPaddingRight : '';
			paddingBottom = options.contentPaddingBottom && options.contentPaddingBottom !== '' ? options.contentPaddingBottom : '';
			paddingLeft = options.contentPaddingLeft && options.contentPaddingLeft !== '' ? options.contentPaddingLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			paddingUnit = options.mobileContentPaddingUnit && options.mobileContentPaddingUnit !== '' ? options.mobileContentPaddingUnit : 'px';
			paddingTop = options.mobileContentPaddingTop && options.mobileContentPaddingTop !== '' ? options.mobileContentPaddingTop : '';
			paddingRight = options.mobileContentPaddingRight && options.mobileContentPaddingRight !== '' ? options.mobileContentPaddingRight : '';
			paddingBottom = options.mobileContentPaddingBottom && options.mobileContentPaddingBottom !== '' ? options.mobileContentPaddingBottom : '';
			paddingLeft = options.mobileContentPaddingLeft && options.mobileContentPaddingLeft !== '' ? options.mobileContentPaddingLeft : '';
		}

		style['padding-top'] = `${paddingTop}${paddingUnit}`;
		style['padding-right'] = `${paddingRight}${paddingUnit}`;
		style['padding-bottom'] = `${paddingBottom}${paddingUnit}`;
		style['padding-left'] = `${paddingLeft}${paddingUnit}`;
	}

	// button padding
	if (styles.includes('button-padding')) {
		let paddingUnit = 'px';
		let paddingTop = 1;
		let paddingRight = 1;
		let paddingBottom = 1;
		let paddingLeft = 1;

		if (device === 'desktop') {
			paddingUnit = options.buttonPaddingUnit && options.buttonPaddingUnit !== '' ? options.buttonPaddingUnit : 'px';
			paddingTop = options.buttonPaddingTop && options.buttonPaddingTop !== '' ? options.buttonPaddingTop : '';
			paddingRight = options.buttonPaddingRight && options.buttonPaddingRight !== '' ? options.buttonPaddingRight : '';
			paddingBottom = options.buttonPaddingBottom && options.buttonPaddingBottom !== '' ? options.buttonPaddingBottom : '';
			paddingLeft = options.buttonPaddingLeft && options.buttonPaddingLeft !== '' ? options.buttonPaddingLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			paddingUnit = options.mobileButtonPaddingUnit && options.mobileButtonPaddingUnit !== '' ? options.mobileButtonPaddingUnit : 'px';
			paddingTop = options.mobileButtonPaddingTop && options.mobileButtonPaddingTop !== '' ? options.mobileButtonPaddingTop : '';
			paddingRight = options.mobileButtonPaddingRight && options.mobileButtonPaddingRight !== '' ? options.mobileButtonPaddingRight : '';
			paddingBottom = options.mobileButtonPaddingBottom && options.mobileButtonPaddingBottom !== '' ? options.mobileButtonPaddingBottom : '';
			paddingLeft = options.mobileButtonPaddingLeft && options.mobileButtonPaddingLeft !== '' ? options.mobileButtonPaddingLeft : '';
		}

		style['padding-top'] = `${paddingTop}${paddingUnit}`;
		style['padding-right'] = `${paddingRight}${paddingUnit}`;
		style['padding-bottom'] = `${paddingBottom}${paddingUnit}`;
		style['padding-left'] = `${paddingLeft}${paddingUnit}`;
	}

	// margin
	if (styles.includes('margin')) {
		let marginUnit = 'px';
		let marginTop = 1;
		let marginRight = 1;
		let marginBottom = 1;
		let marginLeft = 1;

		if (device === 'desktop') {
			marginUnit = options.marginUnit && options.marginUnit !== '' ? options.marginUnit : 'px';
			marginTop = options.marginTop && options.marginTop !== '' ? options.marginTop : '';
			marginRight = options.marginRight && options.marginRight !== '' ? options.marginRight : '';
			marginBottom = options.marginBottom && options.marginBottom !== '' ? options.marginBottom : '';
			marginLeft = options.marginLeft && options.marginLeft !== '' ? options.marginLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			marginUnit = options.mobileMarginUnit && options.mobileMarginUnit !== '' ? options.mobileMarginUnit : 'px';
			marginTop = options.mobileMarginTop && options.mobileMarginTop !== '' ? options.mobileMarginTop : '';
			marginRight = options.mobileMarginRight && options.mobileMarginRight !== '' ? options.mobileMarginRight : '';
			marginBottom = options.mobileMarginBottom && options.mobileMarginBottom !== '' ? options.mobileMarginBottom : '';
			marginLeft = options.mobileMarginLeft && options.mobileMarginLeft !== '' ? options.mobileMarginLeft : '';
		}

		style['margin-top'] = `${marginTop}${marginUnit}`;
		style['margin-right'] = `${marginRight}${marginUnit}`;
		style['margin-bottom'] = `${marginBottom}${marginUnit}`;
		style['margin-left'] = `${marginLeft}${marginUnit}`;
	}

	// title margin
	if (styles.includes('title-margin')) {
		let marginUnit = 'px';
		let marginTop = 1;
		let marginRight = 1;
		let marginBottom = 1;
		let marginLeft = 1;

		if (device === 'desktop') {
			marginUnit = options.titleMarginUnit && options.titleMarginUnit !== '' ? options.titleMarginUnit : 'px';
			marginTop = options.titleMarginTop && options.titleMarginTop !== '' ? options.titleMarginTop : '';
			marginRight = options.titleMarginRight && options.titleMarginRight !== '' ? options.titleMarginRight : '';
			marginBottom = options.titleMarginBottom && options.titleMarginBottom !== '' ? options.titleMarginBottom : '';
			marginLeft = options.titleMarginLeft && options.titleMarginLeft !== '' ? options.titleMarginLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			marginUnit = options.mobileTitleMarginUnit && options.mobileTitleMarginUnit !== '' ? options.mobileTitleMarginUnit : 'px';
			marginTop = options.mobileTitleMarginTop && options.mobileTitleMarginTop !== '' ? options.mobileTitleMarginTop : '';
			marginRight = options.mobileTitleMarginRight && options.mobileTitleMarginRight !== '' ? options.mobileTitleMarginRight : '';
			marginBottom = options.mobileTitleMarginBottom && options.mobileTitleMarginBottom !== '' ? options.mobileTitleMarginBottom : '';
			marginLeft = options.mobileTitleMarginLeft && options.mobileTitleMarginLeft !== '' ? options.mobileTitleMarginLeft : '';
		}

		style['margin-top'] = `${marginTop}${marginUnit}`;
		style['margin-right'] = `${marginRight}${marginUnit}`;
		style['margin-bottom'] = `${marginBottom}${marginUnit}`;
		style['margin-left'] = `${marginLeft}${marginUnit}`;
	}

	// sub title margin
	if (styles.includes('subtitle-margin')) {
		let marginUnit = 'px';
		let marginTop = 1;
		let marginRight = 1;
		let marginBottom = 1;
		let marginLeft = 1;

		if (device === 'desktop') {
			marginUnit = options.subTitleMarginUnit && options.tsubTtleMarginUnit !== '' ? options.tsubTtleMarginUnit : 'px';
			marginTop = options.tsubTtleMarginTop && options.tsubTtleMarginTop !== '' ? options.tsubTtleMarginTop : '';
			marginRight = options.tsubTtleMarginRight && options.tsubTtleMarginRight !== '' ? options.tsubTtleMarginRight : '';
			marginBottom = options.tsubTtleMarginBottom && options.tsubTtleMarginBottom !== '' ? options.tsubTtleMarginBottom : '';
			marginLeft = options.tsubTtleMarginLeft && options.tsubTtleMarginLeft !== '' ? options.tsubTtleMarginLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			marginUnit = options.mobileSubTitleMarginUnit && options.mobileSubTitleMarginUnit !== '' ? options.mobileSubTitleMarginUnit : 'px';
			marginTop = options.mobileSubTitleMarginTop && options.mobileSubTitleMarginTop !== '' ? options.mobileSubTitleMarginTop : '';
			marginRight = options.mobileSubTitleMarginRight && options.mobileSubTitleMarginRight !== '' ? options.mobileSubTitleMarginRight : '';
			marginBottom = options.mobileSubTitleMarginBottom && options.mobileSubTitleMarginBottom !== '' ? options.mobileSubTitleMarginBottom : '';
			marginLeft = options.mobileSubTitleMarginLeft && options.mobileSubTitleMarginLeft !== '' ? options.mobileSubTitleMarginLeft : '';
		}

		style['margin-top'] = `${marginTop}${marginUnit}`;
		style['margin-right'] = `${marginRight}${marginUnit}`;
		style['margin-bottom'] = `${marginBottom}${marginUnit}`;
		style['margin-left'] = `${marginLeft}${marginUnit}`;
	}

	// content margin
	if (styles.includes('content-margin')) {
		let marginUnit = 'px';
		let marginTop = 1;
		let marginRight = 1;
		let marginBottom = 1;
		let marginLeft = 1;

		if (device === 'desktop') {
			marginUnit = options.contentMarginUnit && options.contentMarginUnit !== '' ? options.contentMarginUnit : 'px';
			marginTop = options.contentMarginTop && options.contentMarginTop !== '' ? options.contentMarginTop : '';
			marginRight = options.contentMarginRight && options.contentMarginRight !== '' ? options.contentMarginRight : '';
			marginBottom = options.contentMarginBottom && options.contentMarginBottom !== '' ? options.contentMarginBottom : '';
			marginLeft = options.contentMarginLeft && options.contentMarginLeft !== '' ? options.contentMarginLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			marginUnit = options.mobileContentMarginUnit && options.mobileContentMarginUnit !== '' ? options.mobileContentMarginUnit : 'px';
			marginTop = options.mobileContentMarginTop && options.mobileContentMarginTop !== '' ? options.mobileContentMarginTop : '';
			marginRight = options.mobileContentMarginRight && options.mobileContentMarginRight !== '' ? options.mobileContentMarginRight : '';
			marginBottom = options.mobileContentMarginBottom && options.mobileContentMarginBottom !== '' ? options.mobileContentMarginBottom : '';
			marginLeft = options.mobileContentMarginLeft && options.mobileContentMarginLeft !== '' ? options.mobileContentMarginLeft : '';
		}

		style['margin-top'] = `${marginTop}${marginUnit}`;
		style['margin-right'] = `${marginRight}${marginUnit}`;
		style['margin-bottom'] = `${marginBottom}${marginUnit}`;
		style['margin-left'] = `${marginLeft}${marginUnit}`;
	}

	// button margin
	if (styles.includes('button-margin')) {
		let marginUnit = 'px';
		let marginTop = 1;
		let marginRight = 1;
		let marginBottom = 1;
		let marginLeft = 1;

		if (device === 'desktop') {
			marginUnit = options.buttonMarginUnit && options.buttonMarginUnit !== '' ? options.buttonMarginUnit : 'px';
			marginTop = options.buttonMarginTop && options.buttonMarginTop !== '' ? options.buttonMarginTop : '';
			marginRight = options.buttonMarginRight && options.buttonMarginRight !== '' ? options.buttonMarginRight : '';
			marginBottom = options.buttonMarginBottom && options.buttonMarginBottom !== '' ? options.buttonMarginBottom : '';
			marginLeft = options.buttonMarginLeft && options.buttonMarginLeft !== '' ? options.buttonMarginLeft : '';

		// eslint-disable-next-line
		} else if (device === 'mobile') {
			marginUnit = options.mobileButtonMarginUnit && options.mobileButtonMarginUnit !== '' ? options.mobileButtonMarginUnit : 'px';
			marginTop = options.mobileButtonMarginTop && options.mobileButtonMarginTop !== '' ? options.mobileButtonMarginTop : '';
			marginRight = options.mobileButtonMarginRight && options.mobileButtonMarginRight !== '' ? options.mobileButtonMarginRight : '';
			marginBottom = options.mobileButtonMarginBottom && options.mobileButtonMarginBottom !== '' ? options.mobileButtonMarginBottom : '';
			marginLeft = options.mobileButtonMarginLeft && options.mobileButtonMarginLeft !== '' ? options.mobileButtonMarginLeft : '';
		}

		style['margin-top'] = `${marginTop}${marginUnit}`;
		style['margin-right'] = `${marginRight}${marginUnit}`;
		style['margin-bottom'] = `${marginBottom}${marginUnit}`;
		style['margin-left'] = `${marginLeft}${marginUnit}`;
	}

	// border
	if (styles.includes('border')) {
		style['border-style'] = options.borderStyle || '';
		style['border-color'] = options.borderColor && options.borderColor.rgb && options.borderColor.rgb !== ''
			? getRgbaColor(options.borderColor.rgb)
			: options.borderColor;

		const fixedBorderRadiusUnit = options.borderRadiusUnit && options.borderRadiusUnit !== '' ? options.borderRadiusUnit : 'px';
		style['border-radius'] = options.borderRadius && options.borderRadius !== ''
			? `${options.borderRadius}${fixedBorderRadiusUnit}`
			: '';

		const fixedBorderUnit = options.borderUnit && options.borderUnit !== '' ? options.borderUnit : 'px';
		style['border-top-width'] = options.borderTopWidth && options.borderTopWidth !== ''
			? `${options.borderTopWidth}${fixedBorderUnit}`
			: '';
		style['border-right-width'] = options.borderRightWidth && options.borderRightWidth !== ''
			? `${options.borderRightWidth}${fixedBorderUnit}`
			: '';
		style['border-bottom-width'] = options.borderBottomWidth && options.borderBottomWidth !== ''
			? `${options.borderBottomWidth}${fixedBorderUnit}`
			: '';
		style['border-left-width'] = options.borderLeftWidth && options.borderLeftWidth !== ''
			? `${options.borderLeftWidth}${fixedBorderUnit}`
			: '';
	}

	// title border
	if (styles.includes('title-border')) {
		style['border-style'] = options.titleBorderStyle || '';
		style['border-color'] = options.titleBorderColor && options.titleBorderColor.rgb && options.titleBorderColor.rgb !== ''
			? getRgbaColor(options.titleBorderColor.rgb)
			: options.titleBorderColor;

		const fixedBorderRadiusUnit = options.titleBorderRadiusUnit && options.titleBorderRadiusUnit !== '' ? options.titleBorderRadiusUnit : 'px';
		style['border-radius'] = options.titleBorderRadius && options.titleBorderRadius !== ''
			? `${options.titleBorderRadius}${fixedBorderRadiusUnit}`
			: '';

		const fixedBorderUnit = options.titleBorderUnit && options.titleBorderUnit !== '' ? options.titleBorderUnit : 'px';
		style['border-top-width'] = options.titleBorderTopWidth && options.titleBorderTopWidth !== ''
			? `${options.titleBorderTopWidth}${fixedBorderUnit}`
			: '';
		style['border-right-width'] = options.titleBorderRightWidth && options.titleBorderRightWidth !== ''
			? `${options.titleBorderRightWidth}${fixedBorderUnit}`
			: '';
		style['border-bottom-width'] = options.titleBorderBottomWidth && options.titleBorderBottomWidth !== ''
			? `${options.titleBorderBottomWidth}${fixedBorderUnit}`
			: '';
		style['border-left-width'] = options.titleBorderLeftWidth && options.titleBorderLeftWidth !== ''
			? `${options.titleBorderLeftWidth}${fixedBorderUnit}`
			: '';
	}

	// sub title border
	if (styles.includes('subtitle-border')) {
		style['border-style'] = options.subTitleBorderStyle || '';
		style['border-color'] = options.subTitleBorderColor && options.subTitleBorderColor.rgb && options.subTitleBorderColor.rgb !== ''
			? getRgbaColor(options.subTitleBorderColor.rgb)
			: options.subTitleBorderColor;

		const fixedBorderRadiusUnit = options.subTitleBorderRadiusUnit && options.subTitleBorderRadiusUnit !== '' ? options.subTitleBorderRadiusUnit : 'px';
		style['border-radius'] = options.subTitleBorderRadius && options.subTitleBorderRadius !== ''
			? `${options.subTitleBorderRadius}${fixedBorderRadiusUnit}`
			: '';

		const fixedBorderUnit = options.subTitleBorderUnit && options.subTitleBorderUnit !== '' ? options.subTitleBorderUnit : 'px';
		style['border-top-width'] = options.subTitleBorderTopWidth && options.subTitleBorderTopWidth !== ''
			? `${options.subTitleBorderTopWidth}${fixedBorderUnit}`
			: '';
		style['border-right-width'] = options.subTitleBorderRightWidth && options.subTitleBorderRightWidth !== ''
			? `${options.subTitleBorderRightWidth}${fixedBorderUnit}`
			: '';
		style['border-bottom-width'] = options.subTitleBorderBottomWidth && options.subTitleBorderBottomWidth !== ''
			? `${options.subTitleBorderBottomWidth}${fixedBorderUnit}`
			: '';
		style['border-left-width'] = options.subTitleBorderLeftWidth && options.subTitleBorderLeftWidth !== ''
			? `${options.subTitleBorderLeftWidth}${fixedBorderUnit}`
			: '';
	}

	// content border
	if (styles.includes('content-border')) {
		style['border-style'] = options.contentBorderStyle || '';
		style['border-color'] = options.contentBorderColor && options.contentBorderColor.rgb && options.contentBorderColor.rgb !== ''
			? getRgbaColor(options.contentBorderColor.rgb)
			: options.contentBorderColor;

		const fixedBorderRadiusUnit = options.contentBorderRadiusUnit && options.contentBorderRadiusUnit !== '' ? options.contentBorderRadiusUnit : 'px';
		style['border-radius'] = options.contentBorderRadius && options.contentBorderRadius !== ''
			? `${options.contentBorderRadius}${fixedBorderRadiusUnit}`
			: '';

		const fixedBorderUnit = options.contentBorderUnit && options.contentBorderUnit !== '' ? options.contentBorderUnit : 'px';
		style['border-top-width'] = options.contentBorderTopWidth && options.contentBorderTopWidth !== ''
			? `${options.contentBorderTopWidth}${fixedBorderUnit}`
			: '';
		style['border-right-width'] = options.contentBorderRightWidth && options.contentBorderRightWidth !== ''
			? `${options.contentBorderRightWidth}${fixedBorderUnit}`
			: '';
		style['border-bottom-width'] = options.contentBorderBottomWidth && options.contentBorderBottomWidth !== ''
			? `${options.contentBorderBottomWidth}${fixedBorderUnit}`
			: '';
		style['border-left-width'] = options.contentBorderLeftWidth && options.contentBorderLeftWidth !== ''
			? `${options.contentBorderLeftWidth}${fixedBorderUnit}`
			: '';
	}

	// button border
	if (styles.includes('button-border')) {
		style['border-style'] = options.buttonBorderStyle || '';
		style['border-color'] = options.buttonBorderColor && options.buttonBorderColor.rgb && options.buttonBorderColor.rgb !== ''
			? getRgbaColor(options.buttonBorderColor.rgb)
			: options.buttonBorderColor;

		const fixedBorderRadiusUnit = options.buttonBorderRadiusUnit && options.buttonBorderRadiusUnit !== '' ? options.buttonBorderRadiusUnit : 'px';
		style['border-radius'] = options.buttonBorderRadius && options.buttonBorderRadius !== ''
			? `${options.buttonBorderRadius}${fixedBorderRadiusUnit}`
			: '';

		const fixedBorderUnit = options.buttonBorderUnit && options.buttonBorderUnit !== '' ? options.buttonBorderUnit : 'px';
		style['border-top-width'] = options.buttonBorderTopWidth && options.buttonBorderTopWidth !== ''
			? `${options.buttonBorderTopWidth}${fixedBorderUnit}`
			: '';
		style['border-right-width'] = options.buttonBorderRightWidth && options.buttonBorderRightWidth !== ''
			? `${options.buttonBorderRightWidth}${fixedBorderUnit}`
			: '';
		style['border-bottom-width'] = options.buttonBorderBottomWidth && options.buttonBorderBottomWidth !== ''
			? `${options.buttonBorderBottomWidth}${fixedBorderUnit}`
			: '';
		style['border-left-width'] = options.buttonBorderLeftWidth && options.buttonBorderLeftWidth !== ''
			? `${options.buttonBorderLeftWidth}${fixedBorderUnit}`
			: '';
	}

	// bg color
	if (styles.includes('bg-color')) {
		const isBgGradient = options && options.backgroundColorType && options.backgroundColorType === 'gradient';
		if (isBgGradient) {
			const firstColor = options.backgroundColor && options.backgroundColor.rgb && options.backgroundColor.rgb !== ''
				? rgbToHex(options.backgroundColor.rgb.r, options.backgroundColor.rgb.g, options.backgroundColor.rgb.b)
				: options.backgroundColor;
			const secondColor = options.backgroundSecondColor && options.backgroundSecondColor.rgb && options.backgroundSecondColor.rgb !== ''
				? rgbToHex(options.backgroundSecondColor.rgb.r, options.backgroundSecondColor.rgb.g, options.backgroundSecondColor.rgb.b)
				: '#fff';
			const gradientType = options && options.backgroundGradientType && options.backgroundGradientType && options.backgroundGradientType !== ''
				? options.backgroundGradientType
				: 'linear';
			// eslint-disable-next-line no-nested-ternary
			const gradientDirection = options && options.backgroundGradientDirection && options.backgroundGradientDirection && options.backgroundGradientDirection !== ''
				? gradientType === 'linear'
					? `${options.backgroundGradientDirection}deg`
					: `at ${options.backgroundGradientDirection}`
				: '';
			const gradientStart = options && options.backgroundGradientStart && options.backgroundGradientStart && options.backgroundGradientStart !== ''
				? options.backgroundGradientStart
				: 30;
			const gradientEnd = options && options.backgroundGradientEnd && options.backgroundGradientEnd && options.backgroundGradientEnd !== ''
				? options.backgroundGradientEnd
				: 100;
			const bgGradient = `${gradientType}-gradient(${gradientDirection}, ${firstColor} ${gradientStart}%, ${secondColor} ${gradientEnd}%)`;
			style['background-image'] = bgGradient;
		} else {
			style['background-color'] = options.backgroundColor && options.backgroundColor.rgb && options.backgroundColor.rgb !== ''
				? getRgbaColor(options.backgroundColor.rgb)
				: options.backgroundColor;
		}
	}

	// title bg color
	if (styles.includes('title-bg-color')) {
		style['background-color'] = options.titleBackgroundColor && options.titleBackgroundColor.rgb && options.titleBackgroundColor.rgb !== ''
			? getRgbaColor(options.titleBackgroundColor.rgb)
			: options.titleBackgroundColor;
	}

	// sub title bg color
	if (styles.includes('subtitle-bg-color')) {
		style['background-color'] = options.subTitleBackgroundColor && options.subTitleBackgroundColor.rgb && options.subTitleBackgroundColor.rgb !== ''
			? getRgbaColor(options.subTitleBackgroundColor.rgb)
			: options.subTitleBackgroundColor;
	}

	// content bg color
	if (styles.includes('content-bg-color')) {
		style['background-color'] = options.contentBackgroundColor && options.contentBackgroundColor.rgb && options.contentBackgroundColor.rgb !== ''
			? getRgbaColor(options.contentBackgroundColor.rgb)
			: options.contentBackgroundColor;
	}

	// content bg color
	if (styles.includes('button-bg-color')) {
		style['background-color'] = options.buttonBackgroundColor && options.buttonBackgroundColor.rgb && options.buttonBackgroundColor.rgb !== ''
			? getRgbaColor(options.buttonBackgroundColor.rgb)
			: options.buttonBackgroundColor;
	}

	// button sticky bg color
	if (styles.includes('button-sticky-bg-color')) {
		style['background-color'] = options.buttonStickyBackgroundColor && options.buttonStickyBackgroundColor.rgb && options.buttonStickyBackgroundColor.rgb !== ''
			? getRgbaColor(options.buttonStickyBackgroundColor.rgb)
			: options.buttonStickyBackgroundColor;
	}

	// bg img
	if (styles.includes('bg-img')) {
		if (options.backgroundImageSrc && options.backgroundImageSrc !== '') {
			style['background-image'] = `url(${options.backgroundImageSrc})`;
			style['background-repeat'] = options.backgroundImageRepeat || 'no-repeat';
			style['background-size'] = options.backgroundImageSize || 'auto';
			style['background-position'] = options.backgroundImagePosition || 'center center';
		}
	}

	// box shadow
	if (styles.includes('box-shadow')) {
		if (options.boxShadowActive) {
			const boxShadow = {
				horizontal: 1,
				vertical: 1,
				blur: 10,
				spread: 1,
				color: {
					rgb: {
						a: 1,
						b: 159,
						g: 159,
						r: 161,
					},
				},
			};

			boxShadow.horizontal = options.boxShadowHorizontal || 0;
			boxShadow.vertical = options.boxShadowVertical || 0;
			boxShadow.blur = options.boxShadowBlur || 10;
			boxShadow.spread = options.boxShadowSpread || 1;
			boxShadow.color = options.boxShadowColor && options.boxShadowColor.rgb && options.boxShadowColor.rgb !== ''
				? getRgbaColor(options.boxShadowColor.rgb)
				: options.boxShadowColor;

			style['box-shadow'] = `${boxShadow.horizontal}px ${boxShadow.vertical}px ${boxShadow.blur}px ${boxShadow.spread}px ${boxShadow.color}`;
		}
	}

	// text shadow
	if (styles.includes('text-shadow')) {
		if (options.textShadowActive) {
			const textShadow = {
				horizontal: 1,
				vertical: 1,
				blur: 10,
				color: {
					rgb: {
						a: 1,
						b: 159,
						g: 159,
						r: 161,
					},
				},
			};

			textShadow.horizontal = options.textShadowHorizontal || 0;
			textShadow.vertical = options.textShadowVertical || 0;
			textShadow.blur = options.textShadowBlur || 10;
			textShadow.color = options.textShadowColor && options.textShadowColor.rgb && options.textShadowColor.rgb !== ''
				? getRgbaColor(options.textShadowColor.rgb)
				: options.textShadowColor;

			style['text-shadow'] = `${textShadow.horizontal}px ${textShadow.vertical}px ${textShadow.blur}px ${textShadow.color}`;
		}
	}

	// button sticky box shadow
	if (styles.includes('button-sticky-box-shadow')) {
		if (options.buttonStickyBoxShadowActive) {
			const boxShadow = {
				horizontal: 1,
				vertical: 1,
				blur: 10,
				spread: 1,
				color: {
					rgb: {
						a: 1,
						b: 159,
						g: 159,
						r: 161,
					},
				},
			};

			boxShadow.horizontal = options.buttonStickyBoxShadowHorizontal || 0;
			boxShadow.vertical = options.buttonStickyBoxShadowVertical || 0;
			boxShadow.blur = options.buttonStickyBoxShadowBlur || 10;
			boxShadow.spread = options.buttonStickyBoxShadowSpread || 1;
			boxShadow.color = options.buttonStickyBoxShadowColor && options.buttonStickyBoxShadowColor.rgb && options.buttonStickyBoxShadowColor.rgb !== ''
				? getRgbaColor(options.buttonStickyBoxShadowColor.rgb)
				: options.buttonStickyBoxShadowColor;

			style['box-shadow'] = `${boxShadow.horizontal}px ${boxShadow.vertical}px ${boxShadow.blur}px ${boxShadow.spread}px ${boxShadow.color}`;
		}
	}

	// max width
	if (styles.includes('max-width')) {
		if (device === 'desktop') {
			const fixedMaxWidthUnit = options.maxWidthUnit && options.maxWidthUnit !== '' ? options.maxWidthUnit : '%';
			style['max-width'] = options.maxWidth && options.maxWidth !== ''
				? `${options.maxWidth}${fixedMaxWidthUnit}`
				: '100%';
		} else if (device === 'mobile') {
			const fixedMaxWidthUnit = options.mobileMaxWidthUnit && options.mobileMaxWidthUnit !== '' ? options.mobileMaxWidthUnit : '%';
			style['max-width'] = options.mobileMaxWidth && options.mobileMaxWidth !== ''
				? `${options.mobileMaxWidth}${fixedMaxWidthUnit}`
				: '100%';
		}
	}

	// width
	if (styles.includes('width')) {
		if (device === 'desktop') {
			const fixedWidthUnit = options.widthUnit && options.widthUnit !== '' ? options.widthUnit : '%';
			style.width = options.width && options.width !== ''
				? `${options.width}${fixedWidthUnit}`
				: '';
		} else if (device === 'mobile') {
			const fixedWidthUnit = options.mobileWidthUnit && options.mobileWidthUnit !== '' ? options.mobileWidthUnit : '%';
			style.width = options.mobileWidth && options.mobileWidth !== ''
				? `${options.mobileWidth}${fixedWidthUnit}`
				: '';
		}
	}

	// height
	if (styles.includes('height')) {
		if (device === 'desktop') {
			const fixedHeightUnit = options.heightUnit && options.heightUnit !== '' ? options.heightUnit : '%';
			style.height = options.height && options.height !== ''
				? `${options.height}${fixedHeightUnit}`
				: '100%';
		} else if (device === 'mobile') {
			const fixedMobileHeightUnit = options.mobileHeightUnit && options.mobileHeightUnit !== '' ? options.mobileHeightUnit : '%';
			style.height = options.mobileHeight && options.mobileHeight !== ''
				? `${options.mobileHeight}${fixedMobileHeightUnit}`
				: '100%';
		}
	}

	return style;
};


/**
 * insert an object between object inside array
 *
 * @param {*} arr Origin array
 * @param {*} index Position
 * @param {*} newItem new item
 */
export const insertArr = (arr, index, newItem) => [
	// part of the array before the specified index
	...arr.slice(0, index),
	// inserted item
	newItem,
	// part of the array after the specified index
	...arr.slice(index),
];

export const insertArrayBelow = (array, index, newItem) => {
	const lists = duplicateVar(array);
	const newArr = insertArr(lists, index + 1, newItem);
	return newArr;
};

// Get Midtrans SNAP Options (Callback, ETC)
const getSnapOptions = (paymentChannels, callback) => {
	const options = {
		onSuccess(result) {
			console.log('Payment - Success');
			const statusValid = result.status_code === 200 || result.status_code === '200';
			if (statusValid) {
				if (callback) {
					callback('success', result);
				}
			}
		},
		onPending(result) {
			console.log('Payment - Pending');
			if (callback) {
				callback('pending', result);
			}
			// if (result.pdf_url) {
			// 	window.open(result.pdf_url, '_blank');
			// }
		},
		onError(result) {
			console.log('Payment - Error');
			if (callback) {
				callback('error', result);
			}
		},
		onClose() {
			// console.log('customer closed the popup without finishing the payment');
			if (callback) {
				callback('close');
			}
		},
	};

	if (paymentChannels.length > 0) {
		const isDesktop = !isMobileOrTablet();

		const paymentChannel = paymentChannels[0];

		const qrisChannels = ['ovo', 'dana'];
		if (qrisChannels.includes(paymentChannel)) {
			paymentChannels[0] = PAYMENT_CHANNELS.GOPAY;
		}

		if (paymentChannel === PAYMENT_CHANNELS.SHOPEEPAY && isDesktop) {
			paymentChannels[0] = PAYMENT_CHANNELS.GOPAY;
		}

		options.enabledPayments = paymentChannels;

		let selectedPaymentType = paymentChannel;

		const isQris = (selectedPaymentType === PAYMENT_CHANNELS.GOPAY && isDesktop)
			|| (selectedPaymentType === PAYMENT_CHANNELS.SHOPEEPAY && isDesktop)
			|| selectedPaymentType === PAYMENT_CHANNELS.OVO
			|| selectedPaymentType === PAYMENT_CHANNELS.DANA;

		if (isQris) {
			// selectedPaymentType = 'qris';
			selectedPaymentType = 'gopay';
		}

		// options.selectedPaymentType = selectedPaymentType;

		const gopayMode = isQris ? 'qr' : 'deeplink'; // deeplink, qr, auto
		options.gopayMode = gopayMode;

		if (paymentChannel === PAYMENT_CHANNELS.SHOPEEPAY) {
			options.uiMode = isDesktop ? 'qr' : 'deeplink';
		}
	}

	return options;
};

// Show Midtrans SNAP
export const showMidtransSnap = (snapToken, callback) => {
	if (!window.snap) return;

	const paymentChannels = [
		'bca_va',
		'bni_va',
		'permata_va',
		'other_va',
		'alfamart',
		'echannel',
		'shopeepay',
		'gopay',
	];
	const snapOptions = getSnapOptions(paymentChannels, callback);

	snap.pay(snapToken, snapOptions);
};

// Get Funnel Page URL
export const funnelPageUrl = (username, slug) => {
	const host = window.location.hostname;
	const prefix = host === 'localhost'
		? 'http://localhost:8500' : `https://${username}.ostock.id`;
	const url = `${prefix}/page/${slug}`;
	return url;
};

// Get Browser Name
export const getBrowserName = (key) => {
	// let name = key;
	const names = {
		Chrome: 'chrome',
		Opera: 'opera',
		Firefox: 'firefox',
		Safari: 'safari',
		Edge: 'edge',
		Facebook: 'facebook',
		'Opera Mobile': 'opera-mobile',
		'MIUI Browser': 'mi',
		'Samsung Internet': 'samsung',
		'Vivo Browser': 'vivo',
		'Yandex Browser': 'yandex',
		'HeyTap Browser': 'heytap',
		'Internet Explorer': 'explorer',
		Instagram: 'instagram',
		Unidentified: 'unidentified',
	};
	const name = names[key] || key;
	return name;
};

// Get Device Name
export const getDeviceName = (key) => {
	let name = key;
	if (key === 'desktop') {
		name = 'device-desktop';
	} else if (key === 'mobile') {
		name = 'device-mobile';
	} else if (key === 'tablet') {
		name = 'device-tablet';
	} else if (key === 'Unidentified') {
		name = 'device-unidentified';
	}
	return name;
};

// Get Device Label
export const getDeviceLabel = (key) => {
	let label = key;
	if (key === 'desktop') {
		label = 'Desktop';
	} else if (key === 'mobile') {
		label = 'Mobile';
	} else if (key === 'tablet') {
		label = 'Tablet';
	} else if (key === 'Unidentified') {
		label = 'Unidentified';
	}
	return label;
};

// Format Browser Datas to Series
export const formatBrowserDatasToSeries = (datas) => {
	const chromeRgb = 'rgb(244, 67, 54)';
	const firefoxRgb = 'rgba(255, 143, 0 )';
	const operaRgb = 'rgba(255, 15, 3 )';
	const safariRgb = 'rgba(3, 162, 255 )';
	const unidentifiedRgb = 'rgba(187, 187, 187)';

	const mapper = (curr) => {
		const data = {
			name: curr.label,
			y: curr.data,
		};

		if (curr.name === 'chrome') {
			data.color = chromeRgb;
		} else if (curr.name === 'firefox') {
			data.color = firefoxRgb;
		} else if (curr.name === 'opera') {
			data.color = operaRgb;
		} else if (curr.name === 'safari') {
			data.color = safariRgb;
		} else if (curr.name === 'unidentified') {
			data.color = unidentifiedRgb;
		}

		return data;
	};

	const seriesDatas = datas.map(mapper);
	return seriesDatas;
};

export const getAvailableSectionNames = (tree, sectionNames) => {
	const treeChilds = tree.children;
	const treeChildNames = treeChilds.map(curr => curr.sectionName);
	const names = sectionNames.filter(item => !treeChildNames.includes(item));
	return names;
};

// Simple String Sorter
export const simpleSorter = (a, b) => {
	const nameA = a.toUpperCase(); // ignore upper and lowercase
	const nameB = b.toUpperCase(); // ignore upper and lowercase
	if (nameA < nameB) {
		return -1;
	}
	if (nameA > nameB) {
		return 1;
	}

	// names must be equal
	return 0;
};

// Get Plan Label
export const getPlanLabel = (plan) => {
	const labels = {
		personal: 'Personal',
		professional: 'Professional',
		business: 'Business',
		enterprise: 'enterprise',
		personal_2: 'Personal',
		professional_2: 'Professional',
		business_2: 'Business',
		enterprise_2: 'enterprise',
	};
	const label = labels[plan] || plan;
	return label;
};

export const checkElementRequiredOptions = (tree) => {
	// console.log('tree: jel', tree);
	const ids = [];
	if (tree && tree !== null && tree.children) {
		for (let index = 0; index < tree.children.length; index++) {
			// containers
			const containers = tree.children[index];
			if (containers) {
				const rows = containers.children;

				// row
				if (rows) {
					for (let indexRow = 0; indexRow < rows.length; indexRow++) {
						const row = rows[indexRow];
						const columns = rows[indexRow].children;

						// detect duplicate id
						const rowId = row.id;
						const isIdExist = ids.includes(rowId);
						if (isIdExist) {
							row.id = `row_${generateRandomString()}`;
						} else {
							ids.push(rowId);
						}

						// columns
						if (columns) {
							for (let indexCol = 0; indexCol < columns.length; indexCol++) {
								const col = columns[indexCol];
								const elements = columns[indexCol].children;

								// detect duplicate id
								const colId = col.id;
								const isIdExist = ids.includes(colId);
								if (isIdExist) {
									col.id = `col_${generateRandomString()}`;
								} else {
									ids.push(colId);
								}

								if (elements) {
									for (let indexEl = 0; indexEl < elements.length; indexEl++) {
										const element = elements[indexEl];
										const options = element.options;
										const type = element.type;

										// detect duplicate id
										const elementId = element.id;
										const isIdExist = ids.includes(elementId);
										// console.log('isIdExist: ', isIdExist);
										// console.log('element.id: ', element.id);
										if (isIdExist) {
											const newElementId = `el_${generateRandomString()}__${indexEl++}`;
											element.id = newElementId;
											ids.push(newElementId);
											// console.log('newElementId: ', newElementId);
										} else {
											ids.push(elementId);
										}

										if (options) {
											const defaultElementOptions = duplicateVar(elementOptions);
											const staticElements = defaultElementOptions.find(curr => curr.type === type);
											const staticElementOpts = staticElements.template.options;
											const staticElementListOpts = Object.keys(staticElementOpts);
											if (staticElements) {
												staticElementListOpts.forEach((curr) => {
													// eslint-disable-next-line
													const hasKey = options.hasOwnProperty(curr);
													if (!hasKey) {
														element.options[curr] = staticElementOpts[curr];
													}
												});
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
	return tree;
};

// Pixel Events formatter
export const pixelEventsFormatter = (configs, useDefault = true) => (pixelEvent) => {
	const config = configs.find(({ name }) => name === pixelEvent);
	const defaultValues = useDefault ? defaultPixelConfig : {};
	const newPixelEvent = config || { name: pixelEvent, ...defaultValues };
	return newPixelEvent;
};

export const randomNumber = (min, max) => Math.floor(Math.random() * (max - min)) + min;

export const waLink = (phone, message) => {
	const phoneNumber = phone && phone !== ''
		? formatWaNumber(phone)
		: '62812345678';
	const url = generateWaUrl(phoneNumber, message);
	return url;
};

export const getCurrentElementWidth = (node) => {
	const element = document.querySelector(node);
	const rect = element ? element.getBoundingClientRect() : false;
	const elementWidth = rect && rect.width ? Math.ceil(rect.width) : 0;
	return elementWidth;
};

export const generateVariationsData = (val, multiple) => {
	let item = [];
	if (multiple) {
		val.forEach((variation) => {
			const totalPrice = variation.price * variation.quantity;
			const rowItem = {
				type: 'variation',
				name: variation.value,
				price: totalPrice,
				quantity: variation.quantity,
				singlePrice: variation.price,
			};
			if (variation.wholesale_price) {
				const discountPrice = variation.wholesale_price * variation.quantity;
				rowItem.discountPrice = discountPrice;
			}
			item.push(rowItem);
		});
	} else {
		item = val ? val.split(', ') : [];
	}
	return item;
};

/**
 * @see https://www.py4u.net/discuss/275664
 * @see https://stackoverflow.com/questions/15816815/remove-all-scripts-with-javascript-regex/15816902
 */
export const removeScriptTag = (value) => {
	// const div = document.createElement('div');
	// div.innerHTML = value;
	// const scripts = div.getElementsByTagName('script');
	// let i = scripts.length;
	// while (i--) {
	// 	scripts[i].parentNode.removeChild(scripts[i]);
	// }
	// return div.innerHTML;
	const text = value.replace(/<.*?script.*?>.*?<\/.*?script.*?>/igm, '');
	return text;
};

// Sensor Email Address
export const sensorEmailAddress = (email) => {
	let sensoredEmail = email;
	if (email !== '') {
		const parsedEmail = email.split('@');
		if (parsedEmail[0] && parsedEmail[1]) {
			let address = parsedEmail[0];
			let host = parsedEmail[1];
			if (address.length > 4) {
				address = address.substring(0, 3);
			} else if (address.length > 0) {
				const endIndex = address.length - 1;
				address = address.substring(0, endIndex);
			}
			address = `${address}*****`;
			if (host.length > 4) {
				const startIndex = host.length - 4;
				host = host.substring(startIndex, host.length);
			} else if (host.length > 0) {
				const startIndex = host.length - 1;
				// const maxLength = 1;
				host = host.substring(startIndex, startIndex + 1);
			}
			host = `*****${host}`;
			sensoredEmail = `${address}@${host}`;
		}
	}
	return sensoredEmail;
};

// Generate Timer Label
export const generateTimerLabel = (timerValue) => {
	const minutes = Math.floor(timerValue / 60);
	const minutesLabel = minutes < 10 ? `0${minutes}` : `${minutes}`;
	const seconds = timerValue % 60;
	const secondsLabel = seconds < 10 ? `0${seconds}` : `${seconds}`;
	return `${minutesLabel}:${secondsLabel}`;
};

export const getTicketTypeLabel = (type = '') => {
	const ticketType = ticketsType.find(curr => (curr.value === type ? curr : null));
	return ticketType ? ticketType.label : '-';
};

export const getTicketTopicLabel = (topic = '') => {
	const tiketTopic = ticketsTopic.find(curr => (curr.value === topic ? curr : null));
	return tiketTopic ? tiketTopic.label : '-';
};

export const isDiscountDateValid = (schedule) => {
	let isValid = false;
	if (schedule.enabled) {
		const now = dayjs();
		const dateFormat = 'DD-MM-YYYY HH:mm:ss';
		const startDate = dayjs(schedule.since, dateFormat);
		const endDate = dayjs(schedule.until, dateFormat);
		const isSinceValid = startDate.isValid();
		const isUntilValid = endDate.isValid();
		if (isSinceValid && isUntilValid) {
			const isDateValid = now.isSameOrAfter(startDate, 'day') && now.isSameOrBefore(endDate, 'day');
			isValid = isDateValid;
		}
	} else {
		isValid = true;
	}
	return isValid;
};

export const getExtFile = filename => filename.split('?')[0].split('#')[0].split('.').pop();

export const sortArrayByKey = (array, key) => array.sort((a, b) => (a[key] > b[key] ? 1 : -1));

export const isLocalhost = () => window.location.hostname === 'localhost';

// export const getOstockCommissionValue = (product, price) => {
// 	const commissionPercentage = product.commission_percentage || 5; // default 5%
// 	return price * commissionPercentage / 100;
// };

export const getCommissionValueFormated = (product) => {
	let commissionType = 'percentage';
	let commissionValue = product.commission_percentage || 5;

	if (product.outsource_commission && product.outsource_commission.type && product.outsource_commission.value) {
		commissionType = product.outsource_commission.type;
		commissionValue = product.outsource_commission.value;
	}

	if (
		product?.type === 'variable'
		&& product.variations?.prices[0]?.outsource_commission
		&& product.variations?.prices[0]?.outsource_commission?.type
		&& product.variations?.prices[0]?.outsource_commission?.value
	) {
		commissionType = product.variations?.prices[0]?.outsource_commission?.type;
		commissionValue = product.variations?.prices[0]?.outsource_commission?.value;
	}

	if (commissionType === 'percentage') {
		commissionValue = `${commissionValue}%`;
	} else {
		commissionValue = formatMoney(commissionValue);
	}

	return commissionValue;
};

export const getVariantCommissionValueFormated = (variant) => {
	let commissionType = 'percentage';
	let commissionValue = variant?.commission_percentage || 5;

	if (variant.outsource_commission && variant.outsource_commission.type && variant.outsource_commission.value) {
		commissionType = variant.outsource_commission.type;
		commissionValue = variant.outsource_commission.value;
	}

	if (commissionType === 'percentage') {
		commissionValue = `${commissionValue}%`;
	} else {
		commissionValue = formatMoney(commissionValue);
	}

	return commissionValue;
};

export const getOstockCommissionValue = (product, price) => {
	let commissionValue = 0;
	const commissionPercentage = product.commission_percentage || 5; // default 5%
	commissionValue = price * (commissionPercentage / 100);

	if (product.outsource_commission && product.outsource_commission.type && product.outsource_commission.value) {
		if (product.outsource_commission.type === 'percentage') {
			commissionValue = price * (product.outsource_commission.value / 100);
		} else {
			commissionValue = product.outsource_commission.value;
		}
	}

	return Math.ceil(commissionValue);
};

export const getOstockVariantCommissionValue = (variant, price, product) => {
	let commissionValue = 0;
	const commissionPercentage = product.commission_percentage || 5; // default 5%
	commissionValue = price * (commissionPercentage / 100);

	if (variant.outsource_commission && variant.outsource_commission.type && variant.outsource_commission.value) {
		if (variant.outsource_commission.type === 'percentage') {
			commissionValue = price * (variant.outsource_commission.value / 100);
		} else {
			commissionValue = variant.outsource_commission.value;
		}
	} else if (product.outsource_commission && product.outsource_commission.type && product.outsource_commission.value) {
		if (product.outsource_commission.type === 'percentage') {
			commissionValue = price * (product.outsource_commission.value / 100);
		} else {
			commissionValue = product.outsource_commission.value;
		}
	}

	return Math.ceil(commissionValue);
};

export const getMarginPrice = (product, price, variationPrice = false) => {
	let margin = product.dropship.margin ? product.dropship.margin : 0;
	if (product.dropship.type === 'percentage') {
		margin = price * (product.dropship.margin / 100);
	}
	if (variationPrice && variationPrice.margin) {
		margin = variationPrice.margin;
	}
	return margin;
};

export const getDropshipFinalPrice = (
	product,
	selectedProductVariation = [],
	quantity = 1,
	withMargin = true,
) => {
	let price = 0;
	if (product.type === 'variable') {
		const name = setProductVariationName(selectedProductVariation);
		const productVariationsPrices = product.variations
			? product.variations.prices
			: [];
		const variation = productVariationsPrices.find(curr => curr.name === name);
		if (variation) {
			const variationPrice = variation.price;
			const variationWholesale = variation.wholesale;
			price = getWholesalePrice(variationWholesale, quantity, variationPrice);
		} else if (product.variation_price) {
			price = product.variation_price;
		} else if (product?.variations?.prices[0]?.price) {
			price = product?.variations?.prices[0]?.price;
		}
	} else {
		const isOnDiscount = getIsDiscounted(product);
		const salePrice = getSalePrice(product);
		const wholesales = product.wholesale || [];
		const normalPrice = isOnDiscount ? salePrice : product.price;
		price = getWholesalePrice(wholesales, quantity, normalPrice);
	}

	// add margin to the price
	const margin = withMargin ? getMarginPrice(product, price) : 0;

	// ostock comission
	const commision = getOstockCommissionValue(product, price);

	return price + margin + commision;
};

export const getVariableCommisionValue = (productVariant, product) => {
	let commissionPercentage = 5;
	let commissionValue = 0;
	if (product?.outsource_commission?.type === 'percentage') {
		commissionPercentage = product?.outsource_commission?.value ?? 5;
		commissionValue = product?.price * (commissionPercentage / 100);
	} else {
		commissionValue = product?.outsource_commission?.value ?? product?.price * (commissionPercentage / 100);
	}

	if (productVariant?.outsource_commission?.type === 'percentage') {
		commissionValue = productVariant?.price * (productVariant?.outsource_commission?.value / 100);
	} else if (productVariant?.outsource_commission?.type === 'nominal') {
		commissionValue = productVariant?.outsource_commission?.value ?? productVariant?.price * (commissionPercentage / 100);
	}

	return commissionValue;
};

export const getDropshipVariationFinalPrice = (
	product,
	withWholesale = false,
	withMargin = true,
	// selectedProductVariation = [],
	// quantity = 1,
) => {
	if (product.type === 'variable') {
		const productVariations = duplicateVar(product.variations);
		const result = productVariations.prices.map((variation) => {
			const margin = withMargin ? getMarginPrice(product, variation.price, variation) : 0;
			const commision = getVariableCommisionValue(variation, product);
			const price = variation.price;
			variation.original_price = variation.price;
			variation.price = price + margin + commision;
			variation.cogs = 0;

			if (!withWholesale) variation.wholesale = [];

			return variation;
		});
		productVariations.prices = result;
		// console.log(result);
		return productVariations;
	}
	return product.variations;

	// add margin to the price
	// const margin = getMarginPrice(product, price);
	// return price;
};

export const getProductPricePlusCommission = (product) => {
	const originalPrice = product.price;
	const commissionValue = getOstockCommissionValue(product, originalPrice);

	return originalPrice + commissionValue;
};

export const getProductVariantPricePlusCommission = (variant, product) => {
	const originalPrice = variant.price;
	const commissionValue = getOstockVariantCommissionValue(variant, originalPrice, product);

	return originalPrice + commissionValue;
};

/**
 * Get Value Of Product Change Log
 */
export const getProductChangeLog = (product, key, attribute = 'data') => {
	let isChangeLog = true;
	let result = null;
	if (product) {
		const changeLog = product.change_logs;
		if (changeLog === undefined || !changeLog) {
			isChangeLog = false;
		} else {
			const check = changeLog.find(x => x.data.key === key);
			if (check === undefined) {
				isChangeLog = false;
			}
		}
		if (isChangeLog) {
			if (attribute === 'data') {
				result = changeLog.find(x => x.data.key === key).data.value;
			} else if (attribute === 'active_date') {
				const temp = changeLog.find(x => x.data.key === key);
				result = temp.active_date.date;
			}
		}
	}
	return result;
};

/**
 * Get Product Display Status
 */
export const getProductDisplayStatus = (status, isVerified = false) => {
	let displayStatus = '';
	let color = '';
	switch (status) {
	case 'live':
		displayStatus = 'Active';
		color = 'text-success';
		break;
	case 'pricing':
		displayStatus = 'Pricing';
		color = 'text-warning';
		break;
	case 'recontent':
		displayStatus = 'Recontent';
		color = 'text-warning';
		break;
	case 'request':
		displayStatus = 'Verification';
		color = 'text-warning';
		break;
	case 'reject':
		displayStatus = 'Rejected';
		color = 'text-danger';
		break;
	case 'disabled':
		displayStatus = 'Archived';
		color = 'text-danger';
		break;
	case 'suspended':
		displayStatus = 'Suspended';
		color = 'text-danger';
		break;
	case 'draft':
		displayStatus = 'Draft';
		color = 'text-secondary';
		break;
	default:
		displayStatus = '-';
		color = '';
		break;
	}

	if (status === 'request' && isVerified) {
		displayStatus = 'Updated';
	}

	return {
		status: displayStatus,
		color,
	};
};

/**
 * Get Order/Submission Display Status
 */
export const getOrderDisplayStatus = (order) => {
	let displayStatus = {
		label: '',
		style: '',
	};
	if (order.is_deleted && order.status !== 'refunded' && order.source_type === 'orderonline' && order.shipping.receipt_number) {
		displayStatus = {
			label: 'Cancelled',
			style: 'cancelled',
		};
	} else if (order.status === 'processing' && order.shipping.manifested && !order.shipping.return_status) {
		displayStatus = {
			label: 'Delivery',
			style: 'delivery',
		};
	} else if ((order.status === 'processing' || order.status === 'completed') && order.shipping.delivery_status !== 'delivered' && order.shipping.manifested) {
		if (order.shipping.return_status === 'processing') {
			displayStatus = {
				label: 'On Process Return',
				style: 'process-return',
			};
		} else if (order.shipping.returned_at) {
			displayStatus = {
				label: 'Returned',
				style: 'return',
			};
		}
	} else if (order.status === 'completed' && order.shipping.delivery_status !== 'cancelled') {
		displayStatus = {
			label: 'Delivered',
			style: order.status,
		};
	} else {
		displayStatus = {
			label: order.status,
			style: order.status,
		};
	}
	return displayStatus;
};

/**
 * Get Product Visibility Status
 */
export const getProductVisibilityStatus = (product) => {
	const status = product?.visibility;
	let visibilityStatus = '';
	let color = '';
	switch (status) {
	case 'private':
		visibilityStatus = 'Private';
		color = 'text-warning';
		break;
	case 'public':
		visibilityStatus = 'Public';
		color = 'text-success';
		break;
	default:
		visibilityStatus = 'Public';
		color = 'text-success';
		break;
	}
	return {
		status: visibilityStatus,
		color,
	};
};

export const isHasFeature = (user, feature) => {
	let hasFeature = false;
	const features = user.features;

	if (features !== undefined) {
		hasFeature = features.includes(feature);
	}

	return hasFeature;
};

export const isEmptyObject = obj => Object.keys(obj).length === 0;

export const removeEmojis = (text) => {
	const regex = /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
	text = text.replace(regex, ''); // remove emojis
	text = text.replace(/\|/g, ''); // remove | (pipe character)
	text = text.replace(/[^ -~]+/g, ''); // remove invisible characters
	text = text.trim(); // trim trailing spaces or invisible characters
	return text;
};

export const isUrlValid = (url) => {
	const res = url.match(/(http(s)?:\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi);
	if (res === null) {
		return false; // invalid
	}
	return true; // valid
};

export const getShippingOrigin = (product) => {
	let city = '-';
	try {
		const isShipping = product.shipping.enabled;
		if (isShipping) {
			city = (product.shipping.origin.city_name).replace('Kota ', '');
			city = city.replace('Kab. ', '');
		}
	} catch (error) {
		console.log(error);
	}
	return city;
};

export const getShippingCityProvince = (origin) => {
	let cityProvince = '';
	let city = '';
	let province = '';

	try {
		city = origin?.city?.name;
		province = origin?.province?.name;
		if (city && province) {
			cityProvince = `${city}, ${province}`;
		}
	} catch (error) {
		console.log(error);
	}

	return cityProvince;
};

export const isAssigned = (assignedList, userId) => {
	if (!assignedList || !userId) {
		return false;
	}
	const result = assignedList.find((assigned) => {
		const assignedUserId = assigned?.user_id?.$oid ? assigned?.user_id?.$oid : assigned?.user_id;
		return assignedUserId === userId;
	});
	return !!result; // convert to boolean
};

export const isProductOwner = (productId, userId) => {
	if (!productId || !userId) {
		return false;
	}
	return productId === userId;
};

export const isPrivate = (product, user) => {
	let userId = user?._id;
	if (user?.role !== 'owner') {
		userId = user?.parent_id;
	}

	if (product?.visibility === 'private') {
		const productOwnerId = product?.user?._id?.$oid;
		if (isProductOwner(productOwnerId, userId)) {
			return false;
		}

		if (isAssigned(product?.assigned_user, userId)) {
			return false;
		}

		return true;
	}

	return false;
};

export const sanitizeHtml = rawHtml => DOMPurify.sanitize(rawHtml);

// Turn phone number without country code or zero
export const validatePhoneNumber = (phone) => {
	let validatedPhone = '';

	const regex = /^(?:\+62|62|0)?(\d+)$/; // remove +62, 62 or 0 in front of phone number
	const matches = phone.match(regex);

	if (matches) {
		validatedPhone = matches[1];
	} else {
		validatedPhone = phone;
	}

	return validatedPhone;
};

export const jsonPretty = (val, indent = 4) => {
	if (typeof val === 'string') {
		try {
			val = JSON.parse(val);
		} catch (err) {
			return val;
		}
	}

	if (typeof val === 'object') {
		return JSON.stringify(val, null, indent);
	}

	return val;
};

export const setMaximumStock = (value, maxLength = 6) => {
	let result = value;

	if (value.length > maxLength) {
		result = 999999;
	}

	return result;
};

export const getMinMaxValue = (variations, field = 'price') => {
	let minValue = null;
	let maxValue = null;
	(variations.prices).forEach((element) => {
		if (minValue == null) {
			minValue = element[field];
		} else if (minValue > element[field]) {
			minValue = element[field];
		}
		if (maxValue == null) {
			maxValue = element[field];
		} else if (maxValue < element[field]) {
			maxValue = element[field];
		}
	});

	return {
		minValue,
		maxValue,
	};
};

export const getUomByVariation = (product, item) => {
	let uom = defaultUom;
	const type = product?.type ?? 'simple';
	let variation = product?.variations?.prices?.find(sub => sub?.name === item?.variation);

	if (!variation) {
		variation = product?.variations?.prices?.find(sub => sub?.id === item?.selected_variation);
	}

	if (type === 'variable') {
		if (variation) {
			uom = variation.uom;
		}
	} else {
		uom = product?.uom;
	}

	return uom;
};

export const formatDateWithTime = date => moment(date).format('DD-MM-YYYY HH:mm:ss');

export const getDateNow = () => {
	const dateTimeNow = moment.tz();

	// Format tanggal dan waktu
	const formattedDateTime = dateTimeNow.format('YYYY-MM-DD HH:mm:ss');

	return formattedDateTime;
};
